import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Search,
  FileUp,
  Download,
  Eye,
  Edit,
  Upload,
  Check,
  X,
  Settings,
  Filter,
  MoreVertical,
  LayoutGrid,
  List,
} from "lucide-react";
import * as XLSX from "xlsx";
import toast from 'react-hot-toast';
import InvestmentDeclaration from "./InvestmentDeclaration";
import InvestmentDeclarationModal from "./InvestmentDeclarationModal";
import PayrollReports from "./PayrollReports";
import StandardFilter from "../Common/StandardFilter";
import ActionInsightsPanel from "./ActionInsightsPanel";
import AttachmentPreviewModal from "../Common/AttachmentPreviewModal";
import {
  employeeService,
  departmentService,
  designationService,
  branchService,
  gradesService,
  categoryService,
  subDepartmentService,
  payrollService,
  API_URL,
} from "../../services/api";
import api from "../../services/api";

import Filters from "./Filters";
import DatePicker from "../Common/DatePicker";
import { format } from "date-fns";
import { debounce } from "lodash";
import { usePermissions } from "../../rbac/PermissionsContext";

const getMonthRangeValue = (year, month) => {
  const paddedMonth = (month + 1).toString().padStart(2, "0");
  const start = `${year}-${paddedMonth}-01`;
  const endDay = new Date(year, month + 1, 0)
    .getDate()
    .toString()
    .padStart(2, "0");
  const end = `${year}-${paddedMonth}-${endDay}`;
  return `${start}/${end}`;
};

const getFinancialYearFromCalendarMonth = (year, month) => {
  if (month >= 3) {
    const end = (year + 1) % 100;
    return `${year}-${String(end).padStart(2, "0")}`;
  }
  const end = year % 100;
  return `${year - 1}-${String(end).padStart(2, "0")}`;
};

const filterEmployeesByCalendarMonth = (employees, year, month) => {
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

  return employees.filter((employee) => {
    const joiningDate = new Date(
      employee.dateOfJoin ||
        employee.joiningDate ||
        employee.joinDate ||
        "1900-01-01"
    );
    if (Number.isNaN(joiningDate.getTime()) || joiningDate > monthEnd) {
      return false;
    }

    const relievingRaw =
      employee.relievedDate ||
      employee.leaveDate ||
      employee.resignationDate ||
      employee.terminationDate ||
      employee.exitDate ||
      employee.endDate ||
      employee.separationDate ||
      employee.dateOfLeaving;

    if (relievingRaw) {
      const relievingDate = new Date(relievingRaw);
      if (
        !Number.isNaN(relievingDate.getTime()) &&
        relievingDate < monthStart
      ) {
        return false;
      }
    }

    return true;
  });
};

export default function Payroll({ darkMode, setCurrentPage, toggleDarkMode }) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const PAYROLL_INSIGHTS_KEY = "payrollActionInsightsLog";
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [gradesList, setGradesList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [subDepartmentsList, setSubDepartmentsList] = useState([]);
  const payrollMonthNow = new Date();
  const [selectedMonth, setSelectedMonth] = useState(payrollMonthNow.getMonth());
  const [selectedYear, setSelectedYear] = useState(payrollMonthNow.getFullYear());
  const [minDate, setMinDate] = useState(null);
  const [monthPickerValue, setMonthPickerValue] = useState(() =>
    getMonthRangeValue(payrollMonthNow.getFullYear(), payrollMonthNow.getMonth())
  );
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const selectedFinancialYear = useMemo(
    () => getFinancialYearFromCalendarMonth(selectedYear, selectedMonth),
    [selectedYear, selectedMonth]
  );

  const shouldShowIncomeTaxGuide = (() => {
    const fy = String(selectedFinancialYear || "").trim();
    const match = fy.match(/^(\d{4})-(\d{2}|\d{4})$/);
    if (!match) return false;
    const startYear = Number(match[1]);
    const endYearRaw = match[2];
    const endYear = endYearRaw.length === 2 ? Number(`${match[1].slice(0, 2)}${endYearRaw}`) : Number(endYearRaw);
    return startYear === 2026 && endYear === 2027;
  })();

  const formatDisplayDate = (value) => {
    if (!value || value === "-") return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      return format(d, "dd-MMM-yy");
    } catch (e) {
      return value;
    }
  };

  // Pagination and view mode states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("list");
  const [sortField, setSortField] = useState("dateOfJoin");
  const [sortDirection, setSortDirection] = useState("asc");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Column width and row height customization
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [openActionDropdown, setOpenActionDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [actionEmployee, setActionEmployee] = useState(null);
  const actionButtonRefs = useRef({});
  const [sidebarImportOpen, setSidebarImportOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState({
    actions: 48,
    slNo: 60,
    empCode: 120,
    name: 200,
    designation: 150,
    department: 150,
    joiningDate: 120,
    exitDate: 120,
    ctc: 120,
    taxRegime: 100,
    status: 120,
  });
  const [rowHeight, setRowHeight] = useState(40);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // 'view' or 'edit'

  // Upload and approval states
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    photo: null,
    fileName: "",
    employeeId: null,
  });
  const [photoUploading, setPhotoUploading] = useState({});
  const [documentStatus, setDocumentStatus] = useState({}); // Track approve/reject status
  const [rejectReasons, setRejectReasons] = useState({}); // Track rejection reasons

  // Tab state for Payroll and Reports
  const [activePayrollTab, setActivePayrollTab] = useState("payroll");
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [activePayrollReportsSubTab, setActivePayrollReportsSubTab] = useState("payslips");
  const [payrollReportsSubTabTouched, setPayrollReportsSubTabTouched] = useState(false);
  const [showPayrollInsights, setShowPayrollInsights] = useState(false);
  const [payrollActionLog, setPayrollActionLog] = useState(() => {
    try {
      const raw = localStorage.getItem(PAYROLL_INSIGHTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [mountTrigger, setMountTrigger] = useState(0); // Trigger to ensure fetch on mount

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [payrollSettingsTab, setPayrollSettingsTab] = useState("general"); // "general" | "investmentDeclaration"
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const canViewPayrollWorkspace =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasScreenAccess("payroll", "payroll_dashboard", "view");
  const canViewPayrollReports =
    isAdmin ||
    hasModuleAccess("payroll", "view") ||
    hasScreenAccess("payroll", "payslips", "view");
  const canExportPayroll =
    isAdmin ||
    hasModuleAccess("payroll", "export") ||
    hasScreenAccess("payroll", "payroll_dashboard", "export") ||
    hasScreenAccess("payroll", "payslips", "export");
  const canImportPayroll =
    isAdmin ||
    hasModuleAccess("payroll", "create") ||
    hasScreenAccess("payroll", "process_payroll", "create");
  const canApprovePayroll =
    isAdmin ||
    hasModuleAccess("payroll", "approve") ||
    hasScreenAccess("payroll", "process_payroll", "approve");
  const canEditPayrollSettings =
    isAdmin ||
    hasModuleAccess("payroll", "edit") ||
    hasScreenAccess("payroll", "salary_structure", "edit") ||
    hasScreenAccess("payroll", "statutory_rates", "edit") ||
    hasScreenAccess("payroll", "formula_manager", "edit");
  const canViewAnyPayrollTab = canViewPayrollWorkspace || canViewPayrollReports;

  useEffect(() => {
    if (!canViewAnyPayrollTab) return;
    if (activePayrollTab === "payroll" && !canViewPayrollWorkspace) {
      setActivePayrollTab("reports");
    } else if (activePayrollTab === "reports" && !canViewPayrollReports) {
      setActivePayrollTab("payroll");
    }
  }, [
    activePayrollTab,
    canViewAnyPayrollTab,
    canViewPayrollReports,
    canViewPayrollWorkspace,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(PAYROLL_INSIGHTS_KEY, JSON.stringify(payrollActionLog));
    } catch {
      /* ignore */
    }
  }, [payrollActionLog]);

  const addPayrollInsight = useCallback(
    ({ action, employeeId, status, pendingAt, approvedBy }) => {
      const emp = (allEmployees || []).find((e) => String(e.id) === String(employeeId));
      const employeeCode =
        emp?.empCode || emp?.emp_code || emp?.employeeCode || emp?.employee_code || "-";
      const when = new Date().toLocaleString();
      setPayrollActionLog((prev) => [
        {
          id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          action,
          employeeCode,
          pendingAt: pendingAt || "Payroll Team",
          status: status || "Pending",
          approvedBy: approvedBy || "-",
          when,
        },
        ...prev,
      ]);
    },
    [allEmployees]
  );

  const payrollInsightItems = useMemo(
    () =>
      payrollActionLog.length
        ? payrollActionLog
        : [
            {
              action: "Payroll Lock",
              employeeCode: "-",
              pendingAt: "Finance Head",
              status: "Pending",
              approvedBy: "-",
              when: "-",
            },
          ],
    [payrollActionLog]
  );
  const [incomeTaxGuidePreview, setIncomeTaxGuidePreview] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: "",
  });
  const incomeTaxGuideBlobUrlRef = useRef(null);
  const incomeTaxGuideDownloadUrl = useMemo(
    () =>
      `${String(API_URL || "").replace(/\/+$/, "")}/income-tax-guide/download`,
    []
  );
  const openIncomeTaxGuidePreview = useCallback(async () => {
    try {
      const response = await api.get("/income-tax-guide", {
        responseType: "blob",
      });
      const blob = response?.data;
      if (!(blob instanceof Blob)) {
        toast.error("Unable to load Income Tax Guide.");
        return;
      }
      if (incomeTaxGuideBlobUrlRef.current) {
        URL.revokeObjectURL(incomeTaxGuideBlobUrlRef.current);
      }
      const blobUrl = URL.createObjectURL(blob);
      incomeTaxGuideBlobUrlRef.current = blobUrl;
      setIncomeTaxGuidePreview({
        isOpen: true,
        url: blobUrl,
        downloadUrl: incomeTaxGuideDownloadUrl,
        fileName: "INCOME TAX GUIDE 2026-27.pdf",
      });
    } catch (error) {
      console.error("Failed to load income tax guide preview:", error);
      toast.error("Unable to open Income Tax Guide.");
    }
  }, [incomeTaxGuideDownloadUrl]);

  const getCompanyCodeForExcel = useCallback(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return "COMPANY";
      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
      const code = String(companyData?.code || "")
        .trim()
        .replace(/[^A-Za-z0-9]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
      return code || "COMPANY";
    } catch {
      return "COMPANY";
    }
  }, []);

  const buildPayrollExcelFilename = useCallback((name) => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    const safeName = String(name || "Export")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
    return `${getCompanyCodeForExcel()}_${safeName}_${mm}_${yyyy}.xlsx`;
  }, [getCompanyCodeForExcel]);
  // Separate API params (for backend queries) from form state (for modal persistence)
  const [appliedFilters, setAppliedFilters] = useState({});
  const [appliedFiltersForm, setAppliedFiltersForm] = useState({});

  // Investment declaration specific filters
  const [selectedTaxRegimes, setSelectedTaxRegimes] = useState([]); // ['OLD', 'NEW']
  const [selectedDeclarationStatuses, setSelectedDeclarationStatuses] = useState([]); // ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', etc.]
  const [taxRegimeDropdownOpen, setTaxRegimeDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Check if any non-default filters are currently active
  // Includes: search, advanced Filters modal, tax regime, declaration status, company selection.
  const hasActiveFilters = useMemo(() => {
    const hasSearch = Boolean(
      typeof debouncedSearchTerm === "string"
        ? debouncedSearchTerm.trim()
        : debouncedSearchTerm
    );

    const hasCompanyFilter =
      typeof selectedCompany === "string" && selectedCompany !== "all";

    const hasInvestmentFilters =
      (selectedTaxRegimes || []).length > 0 ||
      (selectedDeclarationStatuses || []).length > 0;

    const hasAdvancedFilters = Object.entries(appliedFilters || {}).some(([k, v]) => {
      if (k === "dateFilterType") return Boolean(v && v !== "all");
      if (v === undefined || v === null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "string") return v.trim() !== "";
      return true;
    });

    return hasSearch || hasCompanyFilter || hasInvestmentFilters || hasAdvancedFilters;
  }, [
    debouncedSearchTerm,
    selectedCompany,
    selectedTaxRegimes,
    selectedDeclarationStatuses,
    appliedFilters,
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setTaxRegimeDropdownOpen(false);
        setStatusDropdownOpen(false);
      }
    };

    if (taxRegimeDropdownOpen || statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [taxRegimeDropdownOpen, statusDropdownOpen]);

  const clearFilters = () => {
    // Clear both API params and form state
    setAppliedFilters({});
    setAppliedFiltersForm({});
    // Clear investment declaration filters
    setSelectedTaxRegimes([]);
    setSelectedDeclarationStatuses([]);
  };

  // Template download modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  // Download confirmation modal state
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);

  // Investment declaration settings
  const [enableInvestmentDeclaration, setEnableInvestmentDeclaration] =
    useState(false);
  const [investmentDeclarationMode, setInvestmentDeclarationMode] =
    useState("manual"); // 'manual', 'auto', or 'always'
  const [investmentDeclarationFromDate, setInvestmentDeclarationFromDate] =
    useState("20");
  const [investmentDeclarationToDate, setInvestmentDeclarationToDate] =
    useState("25");
  const [
    investmentDeclarationManualEndDate,
    setInvestmentDeclarationManualEndDate,
  ] = useState("");

  // Investment declaration with proof settings
  const [
    enableInvestmentDeclarationWithProof,
    setEnableInvestmentDeclarationWithProof,
  ] = useState(false);
  const [
    investmentDeclarationWithProofMode,
    setInvestmentDeclarationWithProofMode,
  ] = useState("manual"); // 'manual', 'auto', or 'always'
  const [
    investmentDeclarationWithProofMonth,
    setInvestmentDeclarationWithProofMonth,
  ] = useState("1"); // 1-12 for months
  const [
    investmentDeclarationWithProofFromDate,
    setInvestmentDeclarationWithProofFromDate,
  ] = useState("10");
  const [
    investmentDeclarationWithProofToDate,
    setInvestmentDeclarationWithProofToDate,
  ] = useState("25");
  const [
    investmentDeclarationWithProofManualEndDate,
    setInvestmentDeclarationWithProofManualEndDate,
  ] = useState("");

  // Tax regime lock settings (default ON; can be turned off only when investment or proof is ON)
  const [enableTaxRegimeLock, setEnableTaxRegimeLock] = useState(true);

  // Bulk investment declaration headers (tax regime + status) per employee
  const [investmentHeaders, setInvestmentHeaders] = useState({}); // key: employeeId -> header
  const isMountedRef = useRef(false);
  const settingsLoadInFlightRef = useRef(null);

  const formatDateForInput = (value) => {
    if (!value) return "";
    const text = String(value).trim();
    return text.includes("T") ? text.slice(0, 10) : text;
  };

  const normalizeOptionalDate = (value) => {
    if (value == null) return null;
    const text = String(value).trim();
    return text ? text.slice(0, 10) : null;
  };

  // Apply loaded settings to state
  const applyPayrollSettingsToState = (settings) => {
    if (!settings) return;
    setPayrollSettings(settings);
    const getFrontendInvestmentMode = (mode) => {
      switch (mode?.toUpperCase()) {
        case "AUTO_MONTHLY": return "auto";
        case "ALWAYS": return "always";
        case "MANUAL":
        default: return "manual";
      }
    };
    const getFrontendProofMode = (mode) => {
      switch (mode?.toUpperCase()) {
        case "AUTO_YEARLY": return "auto";
        case "ALWAYS": return "always";
        case "MANUAL":
        default: return "manual";
      }
    };
    setEnableInvestmentDeclaration(settings.isInvestmentEnabled || false);
    setInvestmentDeclarationMode(getFrontendInvestmentMode(settings.investmentMode));
    setInvestmentDeclarationFromDate(settings.investmentFromDay?.toString() || "20");
    setInvestmentDeclarationToDate(settings.investmentToDay?.toString() || "31");
    setInvestmentDeclarationManualEndDate(formatDateForInput(settings.investmentEndDate));
    setEnableInvestmentDeclarationWithProof(settings.isProofEnabled || false);
    setInvestmentDeclarationWithProofMode(getFrontendProofMode(settings.proofMode));
    setInvestmentDeclarationWithProofMonth(settings.proofMonth?.toString() || "1");
    setInvestmentDeclarationWithProofFromDate(settings.proofFromDay?.toString() || "10");
    setInvestmentDeclarationWithProofToDate(settings.proofToDay?.toString() || "25");
    setInvestmentDeclarationWithProofManualEndDate(formatDateForInput(settings.proofEndDate));
    setEnableTaxRegimeLock(settings.isTaxRegimeLocked !== false);
  };

  // Function to load payroll settings from backend
  const loadPayrollSettings = async () => {
    if (settingsLoadInFlightRef.current) {
      return settingsLoadInFlightRef.current;
    }

    const loadPromise = (async () => {
      setLoadingSettings(true);
      try {
        const response = await payrollService.getPayrollSettings();

        if (response.success && response.data) {
          applyPayrollSettingsToState(response.data);
        } else if (response.success === true && response.data === null) {
          // First-time company: keep form defaults; create happens on explicit Save
          setPayrollSettings(null);
        } else {
          setPayrollSettings(null);
        }
      } catch (error) {
        setPayrollSettings(null);
        if (error?.response?.status !== 404) {
          console.error("Error loading payroll settings:", error);
        }
      } finally {
        setLoadingSettings(false);
        settingsLoadInFlightRef.current = null;
      }
    })();

    settingsLoadInFlightRef.current = loadPromise;
    return loadPromise;
  };

  // Helper function to get name by ID from list
  const getNameByIdFromList = (id, list) => {
    if (!id || !list) return "N/A";
    const item = list.find((item) => item.id === id);
    return item ? item.name : "N/A";
  };

  // Helper function to get month name by number
  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[parseInt(monthNumber) - 1] || "January";
  };

  // Helper function to generate unified template data with both tax regimes
  const generateUnifiedTemplateData = () => {
    return {
      "Sl No": 1,
      "Employee Code": "EMP001",
      "Employee Name": "John Doe",
      "Tax Regime": "Old", // Default to Old, user can change

      // HRA Section - Old Regime Columns
      "HRA - City Type (Old)": "",
      "HRA - Rent Paid (Old)": "",
      "HRA - Landlord PAN (Old)": "",
      "HRA - Address (Old)": "",

      // Section 80C - Old Regime Columns
      "80C - Life Insurance Premium (Old)": "",
      "80C - PPF Contribution (Old)": "",
      "80C - VPF Contribution (Old)": "",
      "80C - Pension Fund (Old)": "",
      "80C - NSC Investment (Old)": "",
      "80C - NSC Interest (Old)": "",
      "80C - ULIP Investment (Old)": "",
      "80C - ELSS Investment (Old)": "",
      "80C - Tuition Fees (Old)": "",
      "80C - Principal Repayment (Old)": "",
      "80C - Registration Charges (Old)": "",
      "80C - Sukanya Samriddhi (Old)": "",
      "80C - Infrastructure Bonds (Old)": "",
      "80C - Fixed Deposit (Old)": "",

      // VIA Deductions - Old Regime Columns
      "VIA - Self Medical Insurance 80D (Old)": "",
      "VIA - Parents Medical Insurance 80D (Old)": "",
      "VIA - Senior Citizen Parents 80D (Old)": "",
      "VIA - Preventive Health Checkup (Old)": "",
      "VIA - Education Loan Interest 80E (Old)": "",
      "VIA - NPS Additional 80CCD1B (Old)": "",
      "VIA - Home Loan Interest 80EE (Old)": "",
      "VIA - Home Loan Interest 80EEA (Old)": "",
      "VIA - Home Loan Interest 80EEB (Old)": "",
      "VIA - Donations 80G (Old)": "",
      "VIA - Savings Interest 80TTA (Old)": "",
      "VIA - Deposit Interest 80TTB (Old)": "",

      // House Property - Old Regime Columns
      "House Property - Property Type (Old)": "",
      "House Property - Loan Before April 1999 (Old)": "",
      "House Property - Interest Paid (Old)": "",
      "House Property - Rental Income (Old)": "",
      "House Property - Address (Old)": "",

      // New Regime Columns (All Empty - No Deductions Applicable)
      "HRA - City Type (New)": "",
      "HRA - Rent Paid (New)": "",
      "HRA - Landlord PAN (New)": "",
      "HRA - Address (New)": "",

      "80C - Life Insurance Premium (New)": "",
      "80C - PPF Contribution (New)": "",
      "80C - VPF Contribution (New)": "",
      "80C - Pension Fund (New)": "",
      "80C - NSC Investment (New)": "",
      "80C - NSC Interest (New)": "",
      "80C - ULIP Investment (New)": "",
      "80C - ELSS Investment (New)": "",
      "80C - Tuition Fees (New)": "",
      "80C - Principal Repayment (New)": "",
      "80C - Registration Charges (New)": "",
      "80C - Sukanya Samriddhi (New)": "",
      "80C - Infrastructure Bonds (New)": "",
      "80C - Fixed Deposit (New)": "",

      "VIA - Self Medical Insurance 80D (New)": "",
      "VIA - Parents Medical Insurance 80D (New)": "",
      "VIA - Senior Citizen Parents 80D (New)": "",
      "VIA - Preventive Health Checkup (New)": "",
      "VIA - Education Loan Interest 80E (New)": "",
      "VIA - NPS Additional 80CCD1B (New)": "",
      "VIA - Home Loan Interest 80EE (New)": "",
      "VIA - Home Loan Interest 80EEA (New)": "",
      "VIA - Home Loan Interest 80EEB (New)": "",
      "VIA - Donations 80G (New)": "",
      "VIA - Savings Interest 80TTA (New)": "",
      "VIA - Deposit Interest 80TTB (New)": "",

      "House Property - Property Type (New)": "",
      "House Property - Loan Before April 1999 (New)": "",
      "House Property - Interest Paid (New)": "",
      "House Property - Rental Income (New)": "",
      "House Property - Address (New)": "",

      // Additional fields
      "Total Deductions": "",
      "Declaration Status": "Not Submitted",
      "Last Updated": "",
      Remarks:
        "Fill Old Regime columns if Tax Regime = Old, leave New Regime columns empty. Fill New Regime columns if Tax Regime = New, leave Old Regime columns empty.",
    };
  };

  // Helper function to get financial year end date
  const getFinancialYearEndDate = (fy) => {
    const year = parseInt(fy.split("-")[1]);
    return new Date(year, 2, 31); // March 31st of the end year
  };

  // Helper function to generate financial years
  const generateFinancialYears = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12

    const years = [];

    // If current month is April or later, current FY is currentYear-currentYear+1
    // If current month is before April, current FY is currentYear-1-currentYear
    const currentFY =
      currentMonth >= 4
        ? `${currentYear}-${currentYear + 1}`
        : `${currentYear - 1}-${currentYear}`;

    // Generate years from 5 years ago to current year only (no future years)
    for (let i = -5; i <= 0; i++) {
      const fyYear = currentMonth >= 4 ? currentYear + i : currentYear - 1 + i;
      const fy = `${fyYear}-${fyYear + 1}`;
      years.push(fy);
    }

    return years.sort().reverse(); // Most recent first
  };

  const monthRangeValue = useMemo(
    () => getMonthRangeValue(selectedYear, selectedMonth),
    [selectedMonth, selectedYear]
  );

  useEffect(() => {
    setMonthPickerValue(monthRangeValue);
  }, [monthRangeValue]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("companyData");
      if (!raw) return;
      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
      const commencesOn = companyData?.serviceCommencesOn;
      if (!commencesOn) return;
      const date = new Date(commencesOn);
      if (Number.isNaN(date.getTime())) return;
      setMinDate(date);
    } catch (e) {
      console.warn("Payroll minDate from companyData.serviceCommencesOn:", e);
    }
  }, []);

  useEffect(() => {
    if (!minDate) return;
    const minYear = minDate.getFullYear();
    const minMonth = minDate.getMonth();
    if (
      selectedYear < minYear ||
      (selectedYear === minYear && selectedMonth < minMonth)
    ) {
      setSelectedYear(minYear);
      setSelectedMonth(minMonth);
      setMonthPickerValue(getMonthRangeValue(minYear, minMonth));
    }
  }, [minDate, selectedMonth, selectedYear]);

  const handleMonthRangeChange = (eventOrValue) => {
    const rawValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue?.target?.value || "";

    const applyMonthChange = (nextYear, nextMonth) => {
      setSelectedYear(nextYear);
      setSelectedMonth(nextMonth);
      setMonthPickerValue(getMonthRangeValue(nextYear, nextMonth));
      setPage(1);
    };

    if (!rawValue) {
      const now = new Date();
      if (now.getFullYear() === selectedYear && now.getMonth() === selectedMonth) {
        return;
      }
      applyMonthChange(now.getFullYear(), now.getMonth());
      return;
    }

    const [start] = rawValue.split("/");
    if (!start) return;

    const [yearString, monthString] = start.split("-");
    const parsedYear = parseInt(yearString, 10);
    const parsedMonth = parseInt(monthString, 10) - 1;

    if (Number.isNaN(parsedYear) || Number.isNaN(parsedMonth)) return;
    if (parsedYear === selectedYear && parsedMonth === selectedMonth) return;

    applyMonthChange(parsedYear, parsedMonth);
  };

  // Helper function to get employee relieving date
  const getEmployeeRelievingDate = (employee) => {
    if (!employee) return null;

    // For terminated employees
    if (employee.employmentStatus === "Terminated") {
      return (
        employee.terminationDate ||
        employee.relievedDate
      );
    }

    // For resigned employees
    if (employee.employmentStatus === "Resigned") {
      return (
        employee.resignationDate ||
        employee.relievedDate
      );
    }

    // For relieved employees
    if (employee.employmentStatus === "Relieved") {
      return (
        employee.relievedDate ||
        employee.dateOfLeaving
      );
    }

    // Default fallback
    return (
      employee.relievedDate ||
      employee.leaveDate ||
      employee.resignationDate ||
      employee.terminationDate ||
      employee.exitDate ||
      employee.endDate ||
      employee.separationDate
    );
  };

  // Filter employees based on financial year and employment status
  const filterEmployeesByFinancialYear = (employees, fy) => {
    const fyEndDate = getFinancialYearEndDate(fy);
    const fyStartDate = new Date(fyEndDate.getFullYear() - 1, 3, 1); // April 1st of start year

    return employees.filter((employee) => {
      const relievingDate = getEmployeeRelievingDate(employee);
      const joiningDate = new Date(
        employee.dateOfJoin ||
          employee.joiningDate ||
          employee.joinDate ||
          "1900-01-01"
      );

      // Basic validation
      if (Number.isNaN(joiningDate.getTime())) {
        return false;
      }

      // Must have joined on or before FY end
      if (joiningDate > fyEndDate) {
        return false;
      }

      // If there is a relieving/exit date and it is before FY start,
      // the employee was not active at any point in this FY
      if (relievingDate) {
        const empRelievingDate = new Date(relievingDate);
        if (!Number.isNaN(empRelievingDate.getTime()) && empRelievingDate < fyStartDate) {
          return false;
        }
      }

      // Otherwise, the employee overlaps with the FY window
      return true;
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchTerm(value);
      }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // Fetch master data on mount
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [
          departmentsResponse,
          designationsResponse,
          branchesResponse,
          gradesResponse,
          categoriesResponse,
          subDepartmentsResponse,
        ] = await Promise.all([
          departmentService.getDepartments(),
          designationService.getDesignations(),
          branchService.getBranches(),
          gradesService.getGrades(),
          categoryService.getAllCategories(),
          subDepartmentService.getAllSubDepartments(),
        ]);

        if (departmentsResponse.data) {
          setDepartmentsList(departmentsResponse.data || []);
        } else if (
          departmentsResponse.success &&
          departmentsResponse.departments
        ) {
          setDepartmentsList(departmentsResponse.departments || []);
        } else {
          setDepartmentsList([]);
        }

        if (designationsResponse.data) {
          setDesignationsList(designationsResponse.data || []);
        } else if (
          designationsResponse.success &&
          designationsResponse.designations
        ) {
          setDesignationsList(designationsResponse.designations || []);
        } else {
          setDesignationsList([]);
        }
        // branches
        if (branchesResponse?.data)
          setBranchesList(branchesResponse.data || []);
        else if (branchesResponse?.success && branchesResponse?.branches)
          setBranchesList(branchesResponse.branches || []);
        else setBranchesList([]);

        // grades
        if (gradesResponse?.data) setGradesList(gradesResponse.data || []);
        else if (gradesResponse?.success && gradesResponse?.grades)
          setGradesList(gradesResponse.grades || []);
        else setGradesList([]);

        // categories
        if (categoriesResponse?.data)
          setCategoriesList(categoriesResponse.data || []);
        else if (categoriesResponse?.success && categoriesResponse?.categories)
          setCategoriesList(categoriesResponse.categories || []);
        else setCategoriesList([]);

        // sub departments
        if (subDepartmentsResponse?.data)
          setSubDepartmentsList(subDepartmentsResponse.data || []);
        else if (
          subDepartmentsResponse?.success &&
          subDepartmentsResponse?.subDepartments
        )
          setSubDepartmentsList(subDepartmentsResponse.subDepartments || []);
        else setSubDepartmentsList([]);
      } catch (error) {
        console.error("Error fetching master data:", error);
      }
    };

    fetchMasterData();
  }, []);

  // Fetch employees without pagination - fetch all employees
  useEffect(() => {
    const fetchEmployees = async () => {
      // Ensure component is initialized before fetching
      if (mountTrigger === 0 && !isMountedRef.current) {
        return;
      }
      
      setIsLoading(true);
      setFetchError(null);
      try {
        const params = {
          page: 1,
          limit: itemsPerPage === "all" ? 10000 : 10000, // Fetch all employees
          search: debouncedSearchTerm,
          sortBy: sortField,
          sortOrder: sortDirection,
          financialYear: selectedFinancialYear,
          excludeEmploymentTypes: "External,External", // Exclude external from payroll listing
          ...appliedFilters,
        };

        // Fetch employees and investment headers in parallel
        const [employeesResponse, headersResponse] = await Promise.allSettled([
          employeeService.getEmployees(params),
          selectedFinancialYear
            ? payrollService.getInvestmentHeaders(selectedFinancialYear)
            : Promise.resolve({ success: false })
        ]);

        // Handle employees response
        if (employeesResponse.status === 'fulfilled' && employeesResponse.value?.success) {
          const data = employeesResponse.value.data || [];
          setAllEmployees(data);
          setTotalEmployees(
            employeesResponse.value.pagination?.totalItems || employeesResponse.value.total || data.length || 0
          );
          setTotalPages(1);
          setFetchError(null);
        } else if (employeesResponse.status === 'rejected') {
          const errorMsg = employeesResponse.reason?.message || employeesResponse.reason?.response?.data?.message || "Failed to fetch employees";
          setFetchError(errorMsg);
          setAllEmployees([]);
          toast.error(errorMsg);
        } else if (employeesResponse.status === 'fulfilled' && !employeesResponse.value?.success) {
          const errorMsg = employeesResponse.value?.message || "Failed to fetch employees";
          setFetchError(errorMsg);
          setAllEmployees([]);
          toast.error(errorMsg);
        } else {
          setAllEmployees([]);
          setFetchError(null);
        }

        // Handle investment headers response
        if (headersResponse.status === 'fulfilled' && headersResponse.value?.success && Array.isArray(headersResponse.value.data)) {
          const map = {};
          headersResponse.value.data.forEach((header) => {
            if (header && header.employeeId != null) {
              map[header.employeeId] = header;
            }
          });
          setInvestmentHeaders(map);
        } else if (headersResponse.status === 'rejected') {
          console.error("Error loading investment headers:", headersResponse.reason);
          // Don't show toast for headers error, just log it
        } else if (!selectedFinancialYear) {
          setInvestmentHeaders({});
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        const errorMsg = error?.message || error?.response?.data?.message || "An unexpected error occurred";
        setFetchError(errorMsg);
        setAllEmployees([]);
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
        isMountedRef.current = true;
      }
    };

    // Always fetch on mount or when dependencies change
    fetchEmployees();
  }, [
    itemsPerPage,
    debouncedSearchTerm,
    selectedFinancialYear,
    selectedMonth,
    selectedYear,
    appliedFilters,
    sortField,
    sortDirection,
    mountTrigger, // Include mount trigger to ensure fetch on component mount
  ]);

  // Initialize component and ensure data loads on mount (when navigating to Payroll page)
  useEffect(() => {
    // Reset state and trigger fetch on every mount/access
    setIsLoading(true);
    setFetchError(null);
    setAllEmployees([]);
    setInvestmentHeaders({});
    isMountedRef.current = false;
    
    // Trigger initial fetch
    setMountTrigger(prev => prev + 1);
    isMountedRef.current = true;
  }, []); // Run only on mount

  // Ensure data loads whenever Payroll route is accessed (navigation, refresh, etc.)
  useEffect(() => {
    // When location pathname includes 'payroll', ensure we're on the Payroll page
    if (location.pathname.includes('/payroll')) {
      // Only trigger fetch if we haven't attempted to load yet (isMountedRef is false)
      // Don't check allEmployees.length as it can be 0 legitimately (no results from search)
      if (!isMountedRef.current && !isLoading) {
        const timer = setTimeout(() => {
          setMountTrigger(prev => prev + 1);
        }, 100);
        return () => clearTimeout(timer);
      }
      // Also ensure we're on the payroll tab
      // if (activePayrollTab !== "payroll") {
      //   setActivePayrollTab("payroll");
      // }
    }
  }, [location.pathname, isLoading, activePayrollTab]);

  // Refetch data when Payroll tab is activated (in case component is reused)
  useEffect(() => {
    if (activePayrollTab === "payroll") {
      // Only trigger fetch if we haven't attempted to load yet
      // Don't check allEmployees.length as it can be 0 legitimately
      if (!isMountedRef.current && !isLoading) {
        const timer = setTimeout(() => {
          setMountTrigger(prev => prev + 1);
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [activePayrollTab, isLoading]);

  // Update filtered employees - Direct pass-through as backend handles filtering
  useEffect(() => {
    setEmployees(allEmployees);
  }, [allEmployees]);

  // Load payroll settings when Payroll is opened: from sidebar (pathname includes payroll) or when Payroll tab is clicked
  useEffect(() => {
    if (location.pathname.includes("/payroll")) loadPayrollSettings();
  }, [location.pathname]);
  useEffect(() => {
    if (activePayrollTab === "payroll") loadPayrollSettings();
  }, [activePayrollTab]);

  // Tax regime lock must stay ON when both investment and proof are OFF.
  // Turning ON investment or proof must never automatically turn the lock OFF.
  useEffect(() => {
    if (!enableInvestmentDeclaration && !enableInvestmentDeclarationWithProof) {
      setEnableTaxRegimeLock(true);
    }
  }, [enableInvestmentDeclaration, enableInvestmentDeclarationWithProof]);

  // Load investment declaration headers for the selected financial year
  // This ensures headers are always available, even if fetchEmployees hasn't completed yet
  const loadInvestmentHeaders = useCallback(async () => {
    try {
      if (!selectedFinancialYear) {
        setInvestmentHeaders({});
        return;
      }

      const response = await payrollService.getInvestmentHeaders(
        selectedFinancialYear
      );

      if (response.success && Array.isArray(response.data)) {
        const map = {};
        response.data.forEach((header) => {
          if (header && header.employeeId != null) {
            map[header.employeeId] = header;
          }
        });
        setInvestmentHeaders(map);
      } else {
        setInvestmentHeaders({});
      }
    } catch (error) {
      console.error("Error loading investment headers for payroll list:", error);
      // Don't show toast for headers error, just log it
      setInvestmentHeaders({});
    }
  }, [selectedFinancialYear]);

  useEffect(() => {
    loadInvestmentHeaders();
  }, [loadInvestmentHeaders]);

  // Helper functions for business logic based on payroll settings
  const isInvestmentDeclarationEnabled = () => {
    return payrollSettings?.isInvestmentEnabled || enableInvestmentDeclaration;
  };

  const isInvestmentProofEnabled = () => {
    return payrollSettings?.isProofEnabled || enableInvestmentDeclarationWithProof;
  };

  const isTaxRegimeLocked = () => {
    return payrollSettings?.isTaxRegimeLocked || enableTaxRegimeLock;
  };

  const getInvestmentDeclarationMode = () => {
    return (payrollSettings?.investmentMode || investmentDeclarationMode).toLowerCase();
  };

  const getInvestmentProofMode = () => {
    return (payrollSettings?.proofMode || investmentDeclarationWithProofMode).toLowerCase();
  };

  // Example usage of payroll settings for business logic:
  // - Use isInvestmentDeclarationEnabled() to show/hide investment declaration features
  // - Use isInvestmentProofEnabled() to show/hide proof upload features  
  // - Use isTaxRegimeLocked() to prevent tax regime changes
  // - Use getInvestmentDeclarationMode() to determine if investment is manual/auto/always
  // - Use getInvestmentProofMode() to determine if proof upload is manual/auto/always

  // Apply client-side financial year filtering for Payroll view
  const filteredEmployeesByMonth = filterEmployeesByCalendarMonth(
    employees,
    selectedYear,
    selectedMonth
  );

  // Apply tax regime and declaration status filters
  const filteredEmployees = useMemo(() => {
    let filtered = filteredEmployeesByMonth;

    // Filter by tax regime
    if (selectedTaxRegimes.length > 0) {
      filtered = filtered.filter((emp) => {
        const header = investmentHeaders[emp.id];
        const regimeRaw = header?.taxRegime || emp.taxRegime || "NEW";
        const regime = regimeRaw.toString().toUpperCase();
        return selectedTaxRegimes.includes(regime);
      });
    }

    // Filter by declaration status
    if (selectedDeclarationStatuses.length > 0) {
      filtered = filtered.filter((emp) => {
        const header = investmentHeaders[emp.id];
        const rawStatus = header?.status;
        
        // Check if filtering for DEFAULT (no status/null/undefined)
        if (selectedDeclarationStatuses.includes("DEFAULT")) {
          // If DEFAULT is selected, include employees with no status
          if (!rawStatus || rawStatus === null || rawStatus === undefined) {
            return true;
          }
        }
        
        // If no status and DEFAULT is not selected, exclude
        if (!rawStatus || rawStatus === null || rawStatus === undefined) {
          return false;
        }
        
        const status = rawStatus.toString().toUpperCase();
        return selectedDeclarationStatuses.includes(status);
      });
    }

    return filtered;
  }, [
    filteredEmployeesByMonth,
    selectedTaxRegimes,
    selectedDeclarationStatuses,
    investmentHeaders,
  ]);

  // Currently sorting is done on backend; keep order but operate on filtered list
  const sortedEmployees = filteredEmployees;

  // Limit employees to the selected count (10, 25, 50, 100, or all)
  const displayEmployees = useMemo(() => {
    if (itemsPerPage === "all") {
      return sortedEmployees;
    }
    const limit = parseInt(itemsPerPage) || 10;
    return sortedEmployees.slice(0, limit);
  }, [sortedEmployees, itemsPerPage]);

  /** Row count for list table viewport height (header + data rows) */
  const tableRowCount =
    itemsPerPage === "all"
      ? displayEmployees.length
      : Math.min(parseInt(itemsPerPage, 10) || 10, displayEmployees.length);

  // Tax regime counts - use filteredEmployees to show filtered count when filters are applied
  const oldRegimeCount = useMemo(() => {
    return filteredEmployees.filter((emp) => {
      const header = investmentHeaders[emp.id];
      const regimeRaw = header?.taxRegime || emp.taxRegime || "NEW";
      const regime = regimeRaw.toString().toUpperCase();
      return regime === "OLD";
    }).length;
  }, [filteredEmployees, investmentHeaders]);
  
  const newRegimeCount = useMemo(() => {
    return filteredEmployees.filter((emp) => {
      const header = investmentHeaders[emp.id];
      const regimeRaw = header?.taxRegime || emp.taxRegime || "NEW";
      const regime = regimeRaw.toString().toUpperCase();
      return regime !== "OLD";
    }).length;
  }, [filteredEmployees, investmentHeaders]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Template download function
  const handleTemplateDownload = () => {
    if (!canExportPayroll) {
      toast.error("You do not have permission to download templates.");
      return;
    }
    try {
      // Generate unified template data with both tax regimes
      const templateData = generateUnifiedTemplateData();

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet([templateData]);

      // Set column widths for all columns
      const colWidths = [
        { wch: 8 }, // Sl No
        { wch: 15 }, // Employee Code
        { wch: 25 }, // Employee Name
        { wch: 12 }, // Tax Regime
        // Old Regime columns
        { wch: 20 }, // HRA fields (Old)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // 80C fields (Old)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // VIA fields (Old)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 }, // House Property fields (Old)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        // New Regime columns
        { wch: 20 }, // HRA fields (New)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // 80C fields (New)
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 }, // VIA fields (New)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 }, // House Property fields (New)
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        // Additional fields
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 50 }, // Remarks
      ];
      ws["!cols"] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Investment Declaration Template");

      // Generate filename with current date
      const filename = buildPayrollExcelFilename(
        "Investment_Declaration_Template_Unified"
      );

      // Save file
      XLSX.writeFile(wb, filename);

      console.log("Unified template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Error downloading template. Please try again.");
    }
  };

  const handleBulkImport = (event) => {
    if (!canImportPayroll) {
      toast.error("You do not have permission to import payroll data.");
      if (event?.target) event.target.value = "";
      return;
    }
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
    ];

    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid Excel file (.xlsx, .xls) or CSV file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        // Get the first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          toast.error("The Excel file appears to be empty or has no data rows.");
          return;
        }

        // Get headers (first row)
        const headers = jsonData[0];

        // Validate required headers
        const requiredHeaders = [
          "Employee Code",
          "Employee Name",
          "Tax Regime",
        ];

        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );

        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(", ")}`);
          return;
        }

        // Process data rows
        const processedData = [];
        const errors = [];

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row.length === 0) continue; // Skip empty rows

          try {
            const employeeCode = row[headers.indexOf("Employee Code")];
            const employeeName = row[headers.indexOf("Employee Name")];
            const taxRegime = row[headers.indexOf("Tax Regime")];

            if (!employeeCode) {
              errors.push(`Row ${i + 1}: Employee Code is required`);
              continue;
            }

            // Find employee by code
            const employee = filteredEmployees.find(
              (emp) => emp.empCode === employeeCode
            );
            if (!employee) {
              errors.push(
                `Row ${i + 1}: Employee with code "${employeeCode}" not found`
              );
              continue;
            }

            // Prepare investment declaration data
            const declarationData = {
              employeeId: employee.id,
              employeeCode: employeeCode,
              taxRegime: taxRegime?.toLowerCase() || "old",

              // HRA Section - Updated to match actual form fields
              houseRent: {
                cityType: row[headers.indexOf("HRA - City Type")] || "metro",
                rentPaid: row[headers.indexOf("HRA - Rent Paid")] || "",
                landlordPan: row[headers.indexOf("HRA - Landlord PAN")] || "",
                address: row[headers.indexOf("HRA - Address")] || "",
              },

              // Section 80C - Updated to match actual form fields
              section80C: {
                lifeInsurance:
                  row[headers.indexOf("80C - Life Insurance Premium")] || "",
                ppf: row[headers.indexOf("80C - PPF Contribution")] || "",
                vpf: row[headers.indexOf("80C - VPF Contribution")] || "",
                pensionFund: row[headers.indexOf("80C - Pension Fund")] || "",
                nsc: row[headers.indexOf("80C - NSC Investment")] || "",
                nscInterest: row[headers.indexOf("80C - NSC Interest")] || "",
                ulip: row[headers.indexOf("80C - ULIP Investment")] || "",
                elss: row[headers.indexOf("80C - ELSS Investment")] || "",
                tuitionFees: row[headers.indexOf("80C - Tuition Fees")] || "",
                houseLoanPrincipal:
                  row[headers.indexOf("80C - Principal Repayment")] || "",
                registrationCharges:
                  row[headers.indexOf("80C - Registration Charges")] || "",
                sukanyaSamriddhi:
                  row[headers.indexOf("80C - Sukanya Samriddhi")] || "",
                infrastructureBonds:
                  row[headers.indexOf("80C - Infrastructure Bonds")] || "",
                fixedDeposit: row[headers.indexOf("80C - Fixed Deposit")] || "",
              },

              // VIA Deductions - Updated to match actual form fields
              sectionVIA: {
                section80D: {
                  self:
                    row[
                      headers.indexOf("VIA - Self Medical Insurance (80D)")
                    ] || "",
                  parents:
                    row[
                      headers.indexOf("VIA - Parents Medical Insurance (80D)")
                    ] || "",
                  parentsSenior:
                    row[
                      headers.indexOf("VIA - Senior Citizen Parents (80D)")
                    ] || "",
                  preventiveHealthCheckup:
                    row[headers.indexOf("VIA - Preventive Health Checkup")] ||
                    "",
                },
                otherInvestments: {
                  section80E:
                    row[
                      headers.indexOf("VIA - Education Loan Interest (80E)")
                    ] || "",
                  section80CCD1B:
                    row[headers.indexOf("VIA - NPS Additional (80CCD1B)")] ||
                    "",
                  section80EE:
                    row[headers.indexOf("VIA - Home Loan Interest (80EE)")] ||
                    "",
                  section80EEA:
                    row[headers.indexOf("VIA - Home Loan Interest (80EEA)")] ||
                    "",
                  section80EEB:
                    row[headers.indexOf("VIA - Home Loan Interest (80EEB)")] ||
                    "",
                  section80G:
                    row[headers.indexOf("VIA - Donations (80G)")] || "",
                  section80TTA:
                    row[headers.indexOf("VIA - Savings Interest (80TTA)")] ||
                    "",
                  section80TTB:
                    row[headers.indexOf("VIA - Deposit Interest (80TTB)")] ||
                    "",
                },
              },

              // House Property - Updated to match actual form fields
              houseProperty: {
                propertyType:
                  row[headers.indexOf("House Property - Property Type")] ||
                  "self-occupied",
                loanTakenBeforeApril1999:
                  row[
                    headers.indexOf("House Property - Loan Before April 1999")
                  ] === "Yes",
                interest:
                  row[headers.indexOf("House Property - Interest Paid")] || "",
                rentalIncome:
                  row[headers.indexOf("House Property - Rental Income")] || "",
                address: row[headers.indexOf("House Property - Address")] || "",
              },

              // Additional fields
              totalDeductions: row[headers.indexOf("Total Deductions")] || "",
              status: row[headers.indexOf("Declaration Status")] || "Submitted",
              remarks: row[headers.indexOf("Remarks")] || "",
              lastUpdated: new Date().toISOString(),
            };

            processedData.push(declarationData);
          } catch (error) {
            errors.push(`Row ${i + 1}: ${error.message}`);
          }
        }

        // Show results
        if (errors.length > 0) {
          const errorMessage = `Import completed with ${
            errors.length
          } errors:\n\n${errors.slice(0, 10).join("\n")}${
            errors.length > 10 ? "\n... and more errors" : ""
          }`;
          toast.error(errorMessage, { duration: 10000 });
        }

        if (processedData.length > 0) {
          console.log("Processed investment declarations:", processedData);
          // TODO: Send processedData to backend API
          toast.success(
            `Successfully processed ${processedData.length} investment declarations. ${errors.length} errors occurred.`
          );
        } else {
          toast.error("No valid data found to import.");
        }
      } catch (error) {
        console.error("Error processing Excel file:", error);
        toast.error(
          "Error processing Excel file. Please check the file format and try again."
        );
      }
    };

    reader.readAsArrayBuffer(file);

    // Reset file input
    event.target.value = "";
  };

  const handleDownload = async () => {
    if (!canExportPayroll) {
      toast.error("You do not have permission to export payroll data.");
      return;
    }
    if (!selectedFinancialYear) {
      toast.error("Please select a financial year to export");
      return;
    }

    // Check if there are any old regime declarations before showing confirmation modal
    const hasOldRegimeData = Object.values(investmentHeaders).some(
      (header) => header && header.taxRegime === "OLD"
    );

    if (!hasOldRegimeData) {
      // No old regime data - show message directly without modal
      toast("No investment declarations found for this financial year", { icon: "ℹ️" });
      return;
    }

    // Show confirmation modal only if there's old regime data
    setShowDownloadConfirm(true);
  };

  const confirmDownload = async () => {
    if (!canExportPayroll) {
      toast.error("You do not have permission to export payroll data.");
      setShowDownloadConfirm(false);
      return;
    }
    try {
      setShowDownloadConfirm(false);
      
      // Pass taxRegime='OLD' to filter only old regime declarations
      const response = await payrollService.exportDeclarations(
        selectedFinancialYear,
        "OLD"
      );
      
      // Check if response is actually JSON (error response) instead of blob
      // When backend returns JSON error with responseType: 'blob', axios converts it to a blob
      // We need to check if it's actually JSON by trying to parse it
      if (response.data instanceof Blob) {
        // Clone the blob before reading (since reading consumes it)
        const blobClone = response.data.slice();
        const text = await blobClone.text();
        
        try {
          const jsonData = JSON.parse(text);
          // If it parses as JSON and has success: false, it's an error response
          if (jsonData.success === false && jsonData.message) {
            toast(jsonData.message, { icon: "ℹ️" });
            return;
          }
        } catch {
          // Not JSON, proceed with blob download
        }
        
        // It's a valid blob, proceed with download
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = buildPayrollExcelFilename(
          "Investment_Declarations_Old_Regime"
        );
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        console.log("Investment declarations exported successfully from backend");
        toast.success("Old Regime investment declarations downloaded successfully");
      }
    } catch (error) {
      console.error("Error exporting investment declarations:", error);
      
      // Check if error response contains a message (when backend returns JSON instead of blob)
      let errorMessage = "Error exporting investment declarations. Please try again.";
      
      if (error.response) {
        // Try to parse the error response
        try {
          // If response.data is a Blob (from axios with responseType: 'blob'), try to read it as text
          if (error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            try {
              const parsed = JSON.parse(text);
              if (parsed.message) {
                errorMessage = parsed.message;
              }
            } catch {
              // Not JSON, use default message
            }
          } else if (error.response.data && typeof error.response.data === 'object') {
            errorMessage = error.response.data.message || errorMessage;
          } else if (typeof error.response.data === 'string') {
            // If it's a string, try to parse it as JSON
            try {
              const parsed = JSON.parse(error.response.data);
              errorMessage = parsed.message || errorMessage;
            } catch {
              // If parsing fails, check if it contains the expected message
              if (error.response.data.includes("No investment declarations found")) {
                errorMessage = "No investment declarations found for this financial year";
              }
            }
          }
        } catch (parseError) {
          console.error("Error parsing error response:", parseError);
        }
      }
      
      // Show the message (info toast for "no data", error toast for actual errors)
      if (errorMessage.includes("No investment declarations found")) {
        toast(errorMessage, { icon: "ℹ️" });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleBulkApprove = () => {
    if (!canApprovePayroll) {
      toast.error("You do not have permission to approve payroll actions.");
      return;
    }
    // TODO: Implement bulk approve logic
    console.log("Bulk approving declarations");
    toast("Bulk approve functionality to be implemented.", { icon: "ℹ️" });
  };

  const handleViewEdit = (employee, mode) => {
    setSelectedEmployee(employee);
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const calculateDropdownPosition = (buttonRect) => {
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const estimatedMenuHeight = 280;
    let top = buttonRect.bottom + scrollY + 4;
    let left = buttonRect.left + scrollX;
    if (top + estimatedMenuHeight > scrollY + viewportHeight) {
      top = buttonRect.top + scrollY - estimatedMenuHeight - 4;
    }
    const menuWidth = 200;
    if (left + menuWidth > viewportWidth + scrollX) {
      left = viewportWidth + scrollX - menuWidth - 10;
    }
    if (left < scrollX + 10) left = scrollX + 10;
    return { top, left };
  };

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (openActionDropdown && actionButtonRefs.current[openActionDropdown]) {
        const button = actionButtonRefs.current[openActionDropdown];
        const rect = button.getBoundingClientRect();
        setDropdownPosition(calculateDropdownPosition(rect));
      }
    };
    if (openActionDropdown) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [openActionDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionDropdown && !event.target.closest(".action-dropdown")) {
        setOpenActionDropdown(null);
      }
    };
    if (openActionDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openActionDropdown]);

  const handlePayrollRowAction = (action, employee) => {
    setOpenActionDropdown(null);
    switch (action) {
      case "salaryRevision":
        toast("Salary revision will open here.", { icon: "ℹ️" });
        break;
      case "taxDeclaration":
        handleViewEdit(employee, "edit");
        break;
      case "loan":
        toast("Loan management will open here.", { icon: "ℹ️" });
        break;
      case "advance":
        toast("Advance management will open here.", { icon: "ℹ️" });
        break;
      case "reimbursement":
        toast("Reimbursement will open here.", { icon: "ℹ️" });
        break;
      case "viewDeclaration":
        handleViewEdit(employee, "view");
        break;
      default:
        break;
    }
  };

  const payrollListRowClasses = (index, isActive) => {
    const borderColor = darkMode
      ? hasActiveFilters
        ? "border-redhalo-navy"
        : "border-[#4C4C4C]"
      : hasActiveFilters
        ? "border-gray-300"
        : "border-[#e4e8ee]";
    const rowBase = darkMode ? "bg-[#2C2C2C]" : "bg-[#fafafa]";
    const activeClass = isActive ? "attendance-row-active" : "";
    return `employee-table-row ${borderColor} border-b ${rowBase} ${activeClass}`;
  };

  // Upload functionality (copied from FamilyDetailsForm.jsx)
  const handlePhotoUpload = async (employeeId, file) => {
    if (file) {
      // Validate file type - allow only images
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/bmp",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error("Only image files (JPG, PNG, GIF, BMP, WebP) are allowed");
        return;
      }

      try {
        setPhotoUploading((prev) => ({ ...prev, [employeeId]: true }));

        // For new employees without ID, store as base64 temporarily
        if (!employeeId) {
          const reader = new FileReader();
          reader.onloadend = () => {
            // Store in local state for display
            setPhotoModal({
              isOpen: true,
              photo: reader.result,
              fileName: file.name,
              employeeId: employeeId,
            });
            setPhotoUploading((prev) => ({ ...prev, [employeeId]: false }));
          };
          reader.readAsDataURL(file);
          return;
        }

        // Upload photo using service for existing employees
        const response = await employeeService.uploadEmployeePhoto(
          employeeId,
          file
        );

        if (response.success) {
          // Update employee data with the file path
          setPhotoModal({
            isOpen: true,
            photo: response.photoPath,
            fileName: file.name,
            employeeId: employeeId,
          });
          console.log(
            "Employee photo uploaded successfully:",
            response.photoPath
          );
          toast.success("Photo uploaded successfully!");
        } else {
          console.error("Employee photo upload failed:", response.message);
          toast.error("Photo upload failed: " + response.message);
        }
      } catch (error) {
        console.error("Error uploading employee photo:", error);
        toast.error("Error uploading photo: " + (error.message || "Unknown error"));
      } finally {
        setPhotoUploading((prev) => ({ ...prev, [employeeId]: false }));
      }
    }
  };

  const handleViewPhoto = (employee) => {
    const photoUrl = `${API_URL.replace(
      /\/api\/?$/,
      ""
    )}/uploads/employee-photos/${employee.user?.photo}`;
    if (employee.user?.photo) {
      // Check if it's a base64 string or file path
      if (
        typeof employee.user.photo === "string" &&
        employee.user.photo.startsWith("data:")
      ) {
        // Base64 photo (new upload)
        setPhotoModal({
          isOpen: true,
          photo: employee.user.photo,
          fileName: employee.user.name || "Employee Photo",
          employeeId: employee.id,
        });
      } else if (typeof employee.user.photo === "string") {
        // File path (uploaded to backend)
        setPhotoModal({
          isOpen: true,
          photo: photoUrl,
          fileName: employee.user.name || "Employee Photo",
          employeeId: employee.id,
        });
      } else {
        // File object (shouldn't happen after our changes, but keeping for safety)
        setPhotoModal({
          isOpen: true,
          photo: photoUrl,
          fileName: employee.user.name || "Employee Photo",
          employeeId: employee.id,
        });
      }
    }
  };

  const closePhotoModal = () => {
    setPhotoModal({
      isOpen: false,
      photo: null,
      fileName: "",
      employeeId: null,
    });
  };

  const downloadPhoto = () => {
    if (photoModal.photo) {
      const link = document.createElement("a");
      link.href = photoModal.photo;
      link.download = photoModal.fileName || "employee-photo";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Approval/Rejection functions
  const handleApprove = (employeeId) => {
    setDocumentStatus((prev) => ({
      ...prev,
      [employeeId]: "approved",
    }));
    addPayrollInsight({
      action: "Investment Declaration",
      employeeId,
      status: "Approved",
      pendingAt: "Payroll Team",
      approvedBy: "Payroll Manager",
    });
    // Clear any rejection reason
    setRejectReasons((prev) => {
      const newReasons = { ...prev };
      delete newReasons[employeeId];
      return newReasons;
    });
  };

  const handleReject = (employeeId, reason) => {
    setDocumentStatus((prev) => ({
      ...prev,
      [employeeId]: "rejected",
    }));
    addPayrollInsight({
      action: "Investment Declaration",
      employeeId,
      status: "Rejected",
      pendingAt: "Payroll Team",
      approvedBy: "Payroll Manager",
    });
    setRejectReasons((prev) => ({
      ...prev,
      [employeeId]: reason,
    }));
  };

  const handleRejectReasonChange = (employeeId, reason) => {
    setRejectReasons((prev) => ({
      ...prev,
      [employeeId]: reason,
    }));
  };

  return (
    <div className="flex h-[calc(100dvh-5rem)] min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div
        className={`flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden ${
          darkMode ? "bg-[#1C1C1C] text-[#E0E0E0]" : "bg-white text-gray-900"
        }`}
      >
        <style>{`
          @keyframes blink-border {
            0%, 100% {
              border-color: rgb(59 130 246);
              box-shadow: 0 0 0 0 rgba(209, 22, 22, 0.7);
            }
            50% {
              border-color: rgb(37 99 235);
              box-shadow: 0 0 0 4px rgba(236, 9, 9, 0);
            }
          }
        `}</style>
        {/* Sub Sidebar — sticky below main nav, in-flow (no overlap of main content); shell matches Employee.jsx */}
        <div className="flex min-h-0 flex-1">
          <aside
            className="sticky top-0 z-20 flex h-full min-h-0 w-56 shrink-0 flex-col overflow-hidden bg-white shadow-sm dark:bg-[#121212]"
          >
            <div className="rh-subnav-aside-head">
              <h4 className="invisible text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400" aria-hidden="true">
                Payroll
              </h4>
            </div>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto border-t border-gray-200 p-2 dark:border-[#3C3C4C]">
              <button
                type="button"
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  activePayrollTab === "payroll"
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
                onClick={() => {
                  setActivePayrollTab("payroll");
                  setReportsMenuOpen(false);
                }}
              >
                Payroll
              </button>
              <button
                type="button"
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  activePayrollTab === "reports"
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
                onClick={() => {
                  setActivePayrollTab("reports");
                  setReportsMenuOpen((prev) => (activePayrollTab === "reports" ? !prev : true));
                }}
              >
                Reports
              </button>
              {reportsMenuOpen && (
                <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                  {[{ id: "payslips", label: "Payslips Report" }].map((report) => (
                    <button
                      key={report.id}
                      type="button"
                      onClick={() => {
                        setActivePayrollTab("reports");
                        setPayrollReportsSubTabTouched(true);
                        setActivePayrollReportsSubTab(report.id);
                      }}
                      className={`block w-full px-3 py-2 text-left text-xs transition-colors ${
                        activePayrollTab === "reports" &&
                        payrollReportsSubTabTouched &&
                        activePayrollReportsSubTab === report.id
                          ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                          : darkMode
                          ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                          : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {report.label}
                    </button>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setSidebarImportOpen((p) => !p)}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                  sidebarImportOpen || showTemplateModal
                    ? "rh-subnav-active"
                    : darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Import
              </button>
              {sidebarImportOpen && (
                <div className="ml-3 border-l border-gray-200 pl-2 dark:border-[#3C3C4C]">
                  <button
                    type="button"
                    onClick={() => {
                      if (!canImportPayroll) {
                        toast.error("You do not have permission to import payroll data.");
                        return;
                      }
                      setShowTemplateModal(true);
                    }}
                    disabled={!canImportPayroll}
                    className={`block w-full px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                      showTemplateModal
                        ? "border-l-[3px] border-l-redhalo-red font-semibold text-redhalo-navy dark:text-redhalo-mint"
                        : darkMode
                        ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                        : "border-l-[3px] border-l-transparent text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Investment Declaration Import
                  </button>
                </div>
              )}

              <button
                type="button"
                onClick={handleDownload}
                disabled={!canExportPayroll}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                  darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Export
              </button>

              <button
                type="button"
                onClick={handleTemplateDownload}
                disabled={!canExportPayroll}
                className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                  darkMode
                    ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                    : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                }`}
              >
                Download Template
              </button>

              {canEditPayrollSettings && (
                <button
                  type="button"
                  onClick={() => {
                    setPayrollSettingsTab("general");
                    setShowSettingsModal(true);
                    loadPayrollSettings();
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                    showSettingsModal
                      ? "rh-subnav-active"
                      : darkMode
                      ? "border-l-[3px] border-l-transparent text-gray-300 hover:bg-gray-800/80"
                      : "border-l-[3px] border-l-transparent text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  Settings
                </button>
              )}
            </div>
          </aside>
          {/* Tab content — flex sibling; aside width reserved in layout (no pl- offset needed) */}
          <div className="relative isolate flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-4">

        {/* Payroll Tab */}
        {!canViewAnyPayrollTab && (
          <div className="flex flex-1 items-center justify-center p-6">
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              You do not have access to Payroll for this role.
            </div>
          </div>
        )}
        {activePayrollTab === "payroll" && canViewPayrollWorkspace && (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            <ActionInsightsPanel
              darkMode={darkMode}
              title="Payroll Action Insights"
              items={payrollInsightItems}
              isFlipped={showPayrollInsights}
              onFlip={() => setShowPayrollInsights((v) => !v)}
              overlayMode="sibling-hide"
            />

            {!showPayrollInsights && (
            <div
              className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-3 ${
                darkMode ? "bg-[#121212]" : "bg-white"
              }`}
            >
            <style>{`
              @keyframes blink-border {
                0%, 100% {
                  border-color: rgb(255 54 54);
                  box-shadow: 0 0 0 0 rgba(255, 54, 54, 0.35);
                }
                50% {
                  border-color: rgb(216 74 70);
                  box-shadow: 0 0 0 4px rgba(255, 54, 54, 0);
                }
              }
            `}</style>
            <div
              className={`shrink-0 px-3 pt-2.5 pb-3 ${
                darkMode
                  ? "bg-[#1C1C1C] border-[#3C3C3C] rounded-t-2xl"
                  : "bg-white border-[#165b5d]/30 rounded-t-2xl"
              } border z-50 shadow-sm`}
            >
            <div className="mt-2.5 flex flex-nowrap items-center gap-2.5 overflow-x-auto">
              <div className="relative w-full min-w-[240px] max-w-md shrink-0">
                <div className="relative">
                  <Search
                    className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 ${
                      darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                    } w-3.5 h-3.5`}
                  />
                  <input
                    type="search"
                    name="employee_filter_query"
                    id="admin-payroll-employee-filter"
                    role="searchbox"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-lpignore="true"
                    placeholder="Search by employee code, name, or mobile number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full rounded-full border py-2 pl-8 pr-8 text-xs transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-redhalo-red/30 ${
                      darkMode
                        ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0] focus:border-redhalo-red"
                        : "border-[#f3d6bf] bg-white text-gray-800 focus:border-[#ef6f2e] hover:border-[#ef6f2e]"
                    } shadow-sm`}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setDebouncedSearchTerm("");
                      }}
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${
                        darkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-400 hover:text-gray-700"
                      }`}
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              <div className="ml-auto flex flex-nowrap items-center gap-2.5 shrink-0">
                {shouldShowIncomeTaxGuide && (
                  <button
                    type="button"
                    onClick={openIncomeTaxGuidePreview}
                    className={`shrink-0 px-2.5 py-1.5 text-xs border rounded-md transition-all ${
                      darkMode
                        ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                    title="Open Income Tax Guide"
                  >
                    Income Tax Guide
                  </button>
                )}
                <div className="flex items-center space-x-1.5 shrink-0">
                  <label
                    className={`text-xs font-medium whitespace-nowrap ${
                      darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                    }`}
                  >
                    Month:
                  </label>
                  <DatePicker
                    compact
                    darkMode={darkMode}
                    monthYearOnly
                    showMonthName
                    key={monthPickerValue}
                    value={monthPickerValue}
                    onChange={handleMonthRangeChange}
                    min={minDate}
                    max={new Date()}
                  />
                </div>

                {/* View Mode Toggle */}
                <button
                  onClick={() =>
                    setViewMode(viewMode === "list" ? "grid" : "list")
                  }
                  className={`px-2.5 py-1.5 border rounded-md text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md ${
                    darkMode
                      ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                      : "bg-white/95 border-[#f3d6bf] text-gray-900 hover:border-[#ef6f2e]"
                  }`}
                  title={`Switch to ${
                    viewMode === "list" ? "Grid" : "List"
                  } View`}
                >
                  {viewMode === "list" ? (
                    <LayoutGrid className="h-4 w-4" />
                  ) : (
                    <List className="h-4 w-4" />
                  )}
                </button>

                {/* Action Buttons */}
                {/* Bulk Import Button */}
                {/* <button
                  onClick={() => setShowTemplateModal(true)}
                  className={`rh-btn-primary flex items-center space-x-2 rounded-md px-3 py-1.5 text-sm transition-all ${
                    darkMode ? "hover:opacity-95" : "hover:shadow-md"
                  }`}
                >
                  <FileUp className="h-4 w-4" />
                  <span>Bulk Import</span>
                </button> */}

                <button
                  type="button"
                  onClick={() => setShowFilterPopup(true)}
                  className={`flex items-center justify-center rh-btn-primary text-sm px-3 py-1.5 transition-all ${
                    hasActiveFilters
                      ? "ring-2 ring-redhalo-red ring-offset-1 dark:ring-offset-[#1C1C1C]"
                      : ""
                  }`}
                  style={
                    hasActiveFilters
                      ? { animation: "blink-border 1.5s ease-in-out infinite" }
                      : undefined
                  }
                  title="Filters"
                  aria-label="Filters"
                >
                  <Filter className="w-4 h-4" aria-hidden />
                </button>

                {/* Investment Declaration Filters */}
                <div className="flex items-center space-x-2 dropdown-container">
                  {/* Tax Regime Filter */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        setTaxRegimeDropdownOpen(!taxRegimeDropdownOpen);
                        setStatusDropdownOpen(false);
                      }}
                      className={`appearance-none px-3 py-1.5 pr-8 border rounded-none text-sm font-medium ${
                        darkMode
                          ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red"
                          : "bg-white border-gray-300 text-gray-900 focus:border-redhalo-red"
                      } transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer`}
                    >
                      {selectedTaxRegimes.length === 0
                        ? "Regime"
                        : selectedTaxRegimes.length === 1
                        ? selectedTaxRegimes[0] === "OLD"
                          ? "Old Regime"
                          : "New Regime"
                        : `${selectedTaxRegimes.length} selected`}
                      <div
                        className={`absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none ${
                          darkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {taxRegimeDropdownOpen && (
                      <div
                        className={`absolute z-50 mt-1 w-48 bg-white rounded-none shadow-lg border ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#3C3C3C]"
                            : "bg-white border-gray-200"
                        } right-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1">
                          {selectedTaxRegimes.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedTaxRegimes([])}
                              className={`w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-red-400 hover:text-red-300 border-[#3C3C3C]"
                                  : "hover:bg-gray-100 text-red-600 hover:text-red-700 border-gray-200"
                              }`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Clear All
                            </button>
                          )}

                          {["OLD", "NEW"].map((regime) => (
                            <label
                              key={regime}
                              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-[#E0E0E0]"
                                  : "hover:bg-gray-100 text-gray-800"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTaxRegimes.includes(regime)}
                                onChange={() => {
                                  setSelectedTaxRegimes((prev) =>
                                    prev.includes(regime)
                                      ? prev.filter((r) => r !== regime)
                                      : [...prev, regime]
                                  );
                                }}
                                className={`w-4 h-4 mr-2 rounded-none border-2 ${
                                  darkMode
                                    ? "bg-[#2C2C2C] border-[#3C3C3C] text-redhalo-red"
                                    : "bg-white border-gray-300 text-redhalo-navy"
                                } focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-0 cursor-pointer`}
                              />
                              <span className="flex-1 cursor-pointer">
                                {regime === "OLD" ? "Old Regime" : "New Regime"}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Declaration Status Filter */}
                  <div className="relative dropdown-container">
                    <button
                      onClick={() => {
                        setStatusDropdownOpen(!statusDropdownOpen);
                        setTaxRegimeDropdownOpen(false);
                      }}
                      className={`appearance-none px-3 py-1.5 pr-8 border rounded-none text-sm font-medium ${
                        darkMode
                          ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red"
                          : "bg-white border-gray-300 text-gray-900 focus:border-redhalo-red"
                      } transition-all duration-200 focus:outline-none shadow-sm hover:shadow-md cursor-pointer`}
                    >
                      {selectedDeclarationStatuses.length === 0
                        ? "Status"
                        : selectedDeclarationStatuses.length === 1
                        ? selectedDeclarationStatuses[0] === "DRAFT"
                          ? "Draft"
                          : selectedDeclarationStatuses[0] === "DEFAULT"
                          ? "Default"
                          : selectedDeclarationStatuses[0].charAt(0) +
                            selectedDeclarationStatuses[0].slice(1).toLowerCase()
                        : `${selectedDeclarationStatuses.length} selected`}
                      <div
                        className={`absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none ${
                          darkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {statusDropdownOpen && (
                      <div
                        className={`absolute z-50 mt-1 w-48 bg-white rounded-none shadow-lg border ${
                          darkMode
                            ? "bg-[#2C2C2C] border-[#3C3C3C]"
                            : "bg-white border-gray-200"
                        } right-0`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1">
                          {selectedDeclarationStatuses.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedDeclarationStatuses([])}
                              className={`w-full flex items-center px-4 py-2 text-sm cursor-pointer border-b ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-red-400 hover:text-red-300 border-[#3C3C3C]"
                                  : "hover:bg-gray-100 text-red-600 hover:text-red-700 border-gray-200"
                              }`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Clear All
                            </button>
                          )}

                          {["DEFAULT", "DRAFT", "SUBMITTED", "APPROVED", "REVIEWED", "REJECTED"].map((status) => (
                            <label
                              key={status}
                              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                                darkMode
                                  ? "hover:bg-[#3C3C3C] text-[#E0E0E0]"
                                  : "hover:bg-gray-100 text-gray-800"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDeclarationStatuses.includes(status)}
                                onChange={() => {
                                  setSelectedDeclarationStatuses((prev) =>
                                    prev.includes(status)
                                      ? prev.filter((s) => s !== status)
                                      : [...prev, status]
                                  );
                                }}
                                className={`w-4 h-4 mr-2 rounded-none border-2 ${
                                  darkMode
                                    ? "bg-[#2C2C2C] border-[#3C3C3C] text-redhalo-red"
                                    : "bg-white border-gray-300 text-redhalo-navy"
                                } focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-0 cursor-pointer`}
                              />
                              <span className="flex-1 cursor-pointer">
                                {status === "DRAFT"
                                  ? "Draft"
                                  : status === "DEFAULT"
                                  ? "Default"
                                  : status.charAt(0) + status.slice(1).toLowerCase()}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Clear All Filters Button */}
                  {(selectedTaxRegimes.length > 0 || selectedDeclarationStatuses.length > 0) && (
                    <button
                      onClick={() => {
                        setSelectedTaxRegimes([]);
                        setSelectedDeclarationStatuses([]);
                      }}
                      className={`px-2.5 py-1.5 text-sm font-medium rounded-none transition-all duration-200 ${
                        darkMode
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-red-500 text-white hover:bg-red-600"
                      } shadow-sm hover:shadow-md`}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>

                {/* Show Options */}
                <div className="flex items-center space-x-2">
                  <label
                    className={`text-xs font-medium ${
                      darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                    }`}
                  >
                    Show:
                  </label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      setItemsPerPage(
                        e.target.value === "all"
                          ? "all"
                          : parseInt(e.target.value)
                      )
                    }
                    className={`px-2 py-1.5 border rounded-md text-xs font-medium transition-all duration-200 focus:outline-none shadow-sm ${
                      darkMode
                        ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] focus:border-redhalo-red"
                        : "bg-white/95 border-[#f3d6bf] text-gray-900 focus:border-[#ef6f2e]"
                    }`}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value="all">All</option>
                  </select>
                </div>

                {/* <button
                  onClick={handleBulkApprove}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm text-white hover:bg-amber-700"
                >
                  Bulk Approve
                </button> */}
              </div>
            </div>
            </div>

            {/* Employee Table/Grid Container — rounded-b-2xl shell; scroll only inside table area (no extra page scrollbar) */}
            <div
              className={`mt-0.5 flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-2xl ${
                darkMode ? "border border-[#3C3C3C]" : "border border-[#165b5d]/30"
              }`}
            >
              <div className="payroll-list-container relative flex flex-1 min-h-0 flex-col overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-redhalo-red"></div>
                    <p
                      className={`text-sm ${
                        darkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Loading employees...
                    </p>
                  </div>
                </div>
              ) : fetchError ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div
                      className={`p-4 rounded-full ${
                        darkMode ? "bg-red-900/20" : "bg-red-100"
                      }`}
                    >
                      <X
                        className={`h-8 w-8 ${
                          darkMode ? "text-red-400" : "text-red-600"
                        }`}
                      />
                    </div>
                    <div className="text-center">
                      <h3
                        className={`text-lg font-medium ${
                          darkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        Error Loading Employees
                      </h3>
                      <p
                        className={`text-sm mt-1 ${
                          darkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {fetchError}
                      </p>
                      <button
                        onClick={() => window.location.reload()}
                        className={`mt-4 px-4 py-2 text-sm rounded-none ${
                          darkMode
                            ? "rh-btn-primary hover:opacity-95"
                            : "rh-btn-primary hover:opacity-95"
                        } transition-colors`}
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div
                      className={`p-4 rounded-full ${
                        darkMode ? "bg-[#3C3C3C]" : "bg-gray-100"
                      }`}
                    >
                      <Search
                        className={`h-8 w-8 ${
                          darkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                      />
                    </div>
                    <div className="text-center">
                      <h3
                        className={`text-lg font-medium ${
                          darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                        }`}
                      >
                        No employees found
                      </h3>
                      <p
                        className={`text-sm mt-1 ${
                          darkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {allEmployees.length === 0
                          ? "No employees available in the system"
                          : "Try adjusting your search or month filter"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
                  {/* List View — vertical scroll only in this region; avoids nested scrollbar on main */}
                  {viewMode === "list" && (
                    <div
                      className={`employee-table-scroll-area flex min-h-0 w-full min-w-0 flex-1 flex-col ${
                        darkMode ? "employee-table-scroll-area-dark" : ""
                      }`}
                    >
                      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-auto">
                        <table
                          className={`min-w-full border-collapse text-sm leading-tight table-fixed employee-table ${
                            darkMode ? "employee-table-dark" : ""
                          } ${
                            darkMode
                              ? "bg-[#2C2C2C] text-[#E0E0E0]"
                              : "bg-[#fafafa] text-gray-800"
                          }`}
                        >
                          <thead
                            className={`sticky top-0 z-40 border-b ${
                              darkMode
                                ? "border-[#4C4C4C] bg-[#3a3a3a]"
                                : "border-[#d7dee8] bg-[#e6eaef]"
                            }`}
                          >
                            <tr style={{ height: `${rowHeight}px` }}>
                              <th
                                className="px-2 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{ width: `${columnWidths.actions}px` }}
                              >
                                Actions
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{ width: `${columnWidths.slNo}px` }}
                                onClick={() => handleSort("slNo")}
                              >
                                Sl No {getSortIcon("slNo")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{ width: `${columnWidths.empCode}px` }}
                                onClick={() => handleSort("empCode")}
                              >
                                Emp Code {getSortIcon("empCode")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{ width: `${columnWidths.name}px` }}
                                onClick={() => handleSort("name")}
                              >
                                Name {getSortIcon("name")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{
                                  width: `${columnWidths.designation}px`,
                                }}
                                onClick={() => handleSort("designation")}
                              >
                                Designation {getSortIcon("designation")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{
                                  width: `${columnWidths.department}px`,
                                }}
                                onClick={() => handleSort("department")}
                              >
                                Department {getSortIcon("department")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{
                                  width: `${columnWidths.joiningDate}px`,
                                }}
                                onClick={() => handleSort("dateOfJoin")}
                              >
                                DOJ {getSortIcon("dateOfJoin")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{ width: `${columnWidths.exitDate}px` }}
                                onClick={() => handleSort("exitDate")}
                              >
                                DOL {getSortIcon("exitDate")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{ width: `${columnWidths.ctc}px` }}
                                onClick={() => handleSort("ctc")}
                              >
                                CTC {getSortIcon("ctc")}
                              </th>
                              <th
                                className={`px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide cursor-pointer ${
                                  darkMode ? "hover:bg-[#4C4C4C]" : "hover:bg-gray-200"
                                }`}
                                style={{ width: `${columnWidths.taxRegime}px` }}
                                onClick={() => handleSort("taxRegime")}
                              >
                                Tax Regime {getSortIcon("taxRegime")}
                              </th>
                              <th
                                className="px-4 py-2 text-center align-middle text-xs font-bold uppercase tracking-wide"
                                style={{ width: `${columnWidths.status}px` }}
                              >
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayEmployees.map((employee, index) => (
                              <tr
                                key={employee.id}
                                className={payrollListRowClasses(
                                  index,
                                  hoveredRowIndex !== null && hoveredRowIndex === index
                                )}
                                style={{ height: `${rowHeight}px` }}
                                onMouseEnter={() => setHoveredRowIndex(index)}
                                onMouseLeave={() => setHoveredRowIndex(null)}
                                onDoubleClick={() => handleViewEdit(employee, "edit")}
                              >
                                  <td
                                    className="px-1 py-1 text-center align-middle"
                                    style={{ width: `${columnWidths.actions}px` }}
                                  >
                                    <div className="relative action-dropdown">
                                      <button
                                        ref={(el) => {
                                          if (el) actionButtonRefs.current[employee.id] = el;
                                        }}
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const button = e.currentTarget;
                                          const rect = button.getBoundingClientRect();
                                          if (openActionDropdown === employee.id) {
                                            setOpenActionDropdown(null);
                                          } else {
                                            setOpenActionDropdown(employee.id);
                                            setActionEmployee(employee);
                                            setDropdownPosition(calculateDropdownPosition(rect));
                                          }
                                        }}
                                        className={`w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center mx-auto ${
                                          darkMode
                                            ? "bg-[#2C2C2C] border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                                            : "bg-gray-100 hover:bg-gray-200"
                                        }`}
                                        title="Payroll actions"
                                      >
                                        <MoreVertical className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                                    style={{ width: `${columnWidths.slNo}px` }}
                                  >
                                    {index + 1}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.empCode}px`,
                                    }}
                                  >
                                    {employee.empCode}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm font-medium"
                                    style={{ width: `${columnWidths.name}px` }}
                                  >
                                    {employee.user?.name}{" "}
                                    {employee.user?.lastName || ""}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.designation}px`,
                                    }}
                                  >
                                    {getNameByIdFromList(
                                      employee.designationId,
                                      designationsList
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.department}px`,
                                    }}
                                  >
                                    {getNameByIdFromList(
                                      employee.departmentId,
                                      departmentsList
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.joiningDate}px`,
                                    }}
                                  >
                                    {formatDisplayDate(
                                      employee.dateOfJoin ||
                                        employee.joiningDate ||
                                        employee.joinDate
                                    )}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm"
                                    style={{
                                      width: `${columnWidths.exitDate}px`,
                                    }}
                                  >
                                    {formatDisplayDate(getEmployeeRelievingDate(employee) || "-")}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap text-sm text-right"
                                    style={{ width: `${columnWidths.ctc}px` }}
                                  >
                                    {employee.ctc
                                      ? employee.ctc.toLocaleString()
                                      : "-"}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap"
                                    style={{
                                      width: `${columnWidths.taxRegime}px`,
                                    }}
                                  >
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const regimeRaw = header?.taxRegime || employee.taxRegime || "NEW";
                                      const regime = regimeRaw.toString().toUpperCase();
                                      const isOld = regime === "OLD";
                                      const label = isOld ? "Old" : "New";
                                      return (
                                        <span
                                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            isOld
                                              ? "rh-accent-soft"
                                              : "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                          }`}
                                        >
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td
                                    className="px-4 py-2 whitespace-nowrap"
                                    style={{
                                      width: `${columnWidths.status}px`,
                                    }}
                                  >
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const rawStatus = header?.status;
                                      
                                      // If no status is available, show "Default"
                                      if (!rawStatus || rawStatus === null || rawStatus === undefined) {
                                        return (
                                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                            Default
                                          </span>
                                        );
                                      }
                                      
                                      const upper = rawStatus.toString().toUpperCase();
                                      let label = rawStatus;
                                      let cls = "bg-gray-100 text-gray-800";

                                      if (upper === "DRAFT") {
                                        label = "Draft";
                                        cls = "rh-accent-soft";
                                      } else if (upper === "APPROVED" || upper === "REVIEWED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300";
                                      } else if (upper === "SUBMITTED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300";
                                      } else if (upper === "REJECTED") {
                                        label = "Rejected";
                                        cls = "bg-red-100 text-red-800";
                                      } else {
                                        // Fallback: keep raw text, neutral color
                                        label = rawStatus;
                                        cls = "bg-gray-100 text-gray-800";
                                      }

                                      return (
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${cls}`}>
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>

                                </tr>
                              ))}
                            </tbody>
                          </table>
                      </div>
                    </div>
                    )}

                    {/* Grid View */}
                    {viewMode === "grid" && (
                      <div className="min-h-0 w-full min-w-0 flex-1 overflow-x-auto overflow-y-auto">
                        <div className="grid grid-cols-4 gap-4 p-4">
                          {displayEmployees.map((employee, index) => (
                            <div
                              key={employee.id}
                              className={`p-3 rounded-none border ${
                                darkMode
                                  ? "bg-[#3C3C3C] border-[#4C4C4C] hover:bg-[#4C4C4C]"
                                  : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                              } transition-colors cursor-pointer mb-2`}
                              onClick={() => handleViewEdit(employee, "view")}
                            >
                              <div className="space-y-1">
                                <div className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                  {employee.empCode}
                                </div>
                                <div className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                  {employee.user?.name}{" "}
                                  {employee.user?.lastName || ""}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
                                  {getNameByIdFromList(
                                    employee.designationId,
                                    designationsList
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                  <div className="flex items-center space-x-1">
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const regimeRaw = header?.taxRegime || employee.taxRegime || "NEW";
                                      const regime = regimeRaw.toString().toUpperCase();
                                      const isOld = regime === "OLD";
                                      return (
                                        <span
                                          className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                            isOld
                                              ? "rh-accent-soft"
                                              : "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                                          }`}
                                        >
                                          {isOld ? "Old" : "New"}
                                        </span>
                                      );
                                    })()}
                                    {(() => {
                                      const header = investmentHeaders[employee.id];
                                      const rawStatus = header?.status;
                                      
                                      // If no status is available, show "Default"
                                      if (!rawStatus || rawStatus === null || rawStatus === undefined) {
                                        return (
                                          <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                            Default
                                          </span>
                                        );
                                      }
                                      
                                      const upper = rawStatus.toString().toUpperCase();
                                      let label = rawStatus;
                                      let cls = "bg-gray-100 text-gray-800";

                                      if (upper === "DRAFT") {
                                        label = "Draft";
                                        cls = "rh-accent-soft";
                                      } else if (upper === "APPROVED" || upper === "REVIEWED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300";
                                      } else if (upper === "SUBMITTED") {
                                        label = upper.charAt(0) + upper.slice(1).toLowerCase();
                                        cls = "border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300";
                                      } else if (upper === "REJECTED") {
                                        label = "Rejected";
                                        cls = "bg-red-100 text-red-800";
                                      } else {
                                        label = rawStatus;
                                        cls = "bg-gray-100 text-gray-800";
                                      }

                                      return (
                                        <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${cls}`}>
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewEdit(employee, "view");
                                      }}
                                      className={`p-1 rounded-md transition-colors ${
                                        darkMode
                                          ? "text-redhalo-mint hover:text-white hover:bg-[#3C3C3C]"
                                          : "text-redhalo-navy hover:text-redhalo-red hover:bg-redhalo-mint/50"
                                      }`}
                                      title="View"
                                    >
                                      <Eye className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewEdit(employee, "edit");
                                      }}
                                      className={`p-1 rounded-md transition-colors ${
                                        darkMode
                                          ? "text-redhalo-mint hover:bg-[#3C3C3C] hover:text-white"
                                          : "text-redhalo-navy hover:bg-redhalo-mint/50 hover:text-redhalo-red"
                                      }`}
                                      title="Edit"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                 </div>
               )}

            {!isLoading && !fetchError && filteredEmployees.length > 0 && (
              <div
                className={`-mx-px w-[calc(100%+2px)] shrink-0 border border-t-0 px-3 py-2 ${
                  darkMode
                    ? "border-[#3C3C4C] bg-[#2C2C2C]"
                    : "border-[#165b5d]/30 bg-gray-50"
                }`}
              >
                <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md border px-2 py-1 text-xs font-semibold tabular-nums tracking-tight ${
                        darkMode
                          ? "border-[#4C4C4C] bg-[#252525] text-[#E0E0E0]"
                          : "border-[#d7dee8] bg-white text-gray-800 shadow-sm"
                      }`}
                      title={
                        Number(totalEmployees) > 0
                          ? `Total employees (reported): ${totalEmployees}. ${displayEmployees.length} row(s) visible in the grid above.`
                          : `Employees in scope for this month: ${filteredEmployees.length}. ${displayEmployees.length} row(s) visible in the grid.`
                      }
                    >
                      <span className="whitespace-nowrap">Listed Employees:</span>{" "}
                      <span className="font-bold">
                        {Number(totalEmployees) > 0
                          ? totalEmployees
                          : filteredEmployees.length}
                      </span>
                      {displayEmployees.length <
                        (Number(totalEmployees) > 0
                          ? Number(totalEmployees)
                          : filteredEmployees.length) && (
                        <span
                          className={`whitespace-nowrap text-[10px] font-normal opacity-80 ${
                            darkMode ? "text-[#A6A9C8]" : "text-gray-500"
                          }`}
                        >
                          ({displayEmployees.length} in grid)
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center justify-end">
                    <div className="flex min-w-0 items-center justify-end space-x-2 overflow-x-auto whitespace-nowrap scrollbar-hide">
                    {hasActiveFilters && (
                      <div
                        className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                          darkMode
                            ? "bg-red-600 text-white"
                            : "bg-red-500 text-white"
                        }`}
                      >
                        Filtered
                      </div>
                    )}
                    <div
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                        darkMode
                          ? "bg-redhalo-navy text-redhalo-mint"
                          : "rh-accent-soft"
                      }`}
                    >
                      Old Regime: {oldRegimeCount}
                    </div>

                    <div
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                        darkMode
                          ? "border border-emerald-800/50 bg-emerald-950/50 text-emerald-300"
                          : "border border-emerald-200 bg-emerald-50 text-emerald-900"
                      }`}
                    >
                      New Regime: {newRegimeCount}
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activePayrollTab === "reports" && canViewPayrollReports && (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <PayrollReports
              key={`payroll-reports-${activePayrollReportsSubTab}`}
              darkMode={darkMode}
              setCurrentPage={setCurrentPage}
              toggleDarkMode={toggleDarkMode}
              forcedSubTab={activePayrollReportsSubTab}
              hideSidebar
            />
          </div>
        )}
          </div>
        </div>

        {openActionDropdown && actionEmployee && (
          <div
            className={`fixed z-[9998] overflow-hidden rounded-lg border shadow-xl action-dropdown ${
              darkMode
                ? "border-[#3C3C3C] bg-[#2C2C2C] text-[#E0E0E0]"
                : "border-gray-200 bg-white text-gray-800"
            }`}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              minWidth: "200px",
            }}
          >
            <div
              className={`flex items-center justify-between border-b px-4 py-2.5 ${
                darkMode
                  ? "border-[#3C3C3C] bg-[#1C1C1C]"
                  : "border-gray-200 bg-gray-panel"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className={`text-xs font-semibold truncate ${darkMode ? "text-[#E0E0E0]" : "text-gray-800"}`}>
                  {actionEmployee.empCode || actionEmployee.employeeId}
                </div>
                <div className={`text-xs truncate ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {actionEmployee.user?.name} {actionEmployee.user?.lastName || ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpenActionDropdown(null)}
                className={`ml-2 rounded p-1 ${darkMode ? "text-red-400 hover:bg-[#3C3C3C]" : "text-red-500 hover:bg-red-50"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              {[
                ["salaryRevision", "Salary Revision"],
                ["taxDeclaration", "Tax Declaration"],
                ["loan", "Loan"],
                ["advance", "Advance"],
                ["reimbursement", "Reimbursement"],
                ["viewDeclaration", "View Declaration"],
              ].map(([action, label], idx) => (
                <button
                  key={action}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePayrollRowAction(action, actionEmployee);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-xs font-medium transition-colors ${
                    idx > 0 ? "border-t" : ""
                  } ${
                    darkMode
                      ? "border-[#3C3C3C] text-[#E0E0E0] hover:bg-[#3C3C3C]"
                      : "border-gray-200 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Modal */}
        {isModalOpen && (
          <InvestmentDeclarationModal
            employee={selectedEmployee}
            mode={modalMode}
            onClose={() => setIsModalOpen(false)}
            onSave={async (updatedData) => {
              console.log("Saving updated data:", updatedData);
              
              // Only close modal if it's a full submission, not for intermediate updates
              if (updatedData?.type === 'submitted' || updatedData?.type === 'submit_success') {
                setIsModalOpen(false);
              }
              
              // Reload investment headers to reflect updated status/regime/attachments
              await loadInvestmentHeaders();
            }}
            payrollSettings={payrollSettings}
            isInvestmentDeclarationEnabled={isInvestmentDeclarationEnabled()}
            isInvestmentProofEnabled={isInvestmentProofEnabled()}
            getInvestmentDeclarationMode={getInvestmentDeclarationMode}
            getInvestmentProofMode={getInvestmentProofMode}
            financialYear={selectedFinancialYear}
          />
        )}

        {/* Settings Modal */}
        {showSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div
              className={`p-6 rounded-none shadow-xl w-[600px] max-h-[80vh] overflow-hidden flex flex-col ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              }`}
            >
              {/* Header */}
              <div className="flex justify-between z-30 items-center mb-6">
                <div className="flex items-center space-x-3">
                  <h2 className="text-xl font-bold">Payroll Settings</h2>
                  {loadingSettings && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-redhalo-red"></div>
                  )}
                </div>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className={`${
                    darkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="flex min-h-0 flex-1">
                <div className="rh-subnav-aside mr-4">
                  <div className="rh-subnav-aside-head">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                      Payroll Settings
                    </h4>
                  </div>
                  <div className="p-2 space-y-1">
                    <button
                      type="button"
                      onClick={() => setPayrollSettingsTab("general")}
                      className={`w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                        payrollSettingsTab === "general"
                          ? "rh-subnav-active"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      General Settings
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayrollSettingsTab("investmentDeclaration")}
                      className={`w-full text-left px-3 py-2 text-sm rounded-none transition-colors ${
                        payrollSettingsTab === "investmentDeclaration"
                          ? "rh-subnav-active"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Investment Declaration Settings
                    </button>
                  </div>
                </div>

                {/* Settings Content */}
                <div className="space-y-6 flex-1 overflow-y-auto min-h-0">
                {payrollSettingsTab === "general" && (
                  /* General Settings: Service Commences On (read-only) */
                  (() => {
                    try {
                      const raw = typeof localStorage !== "undefined" ? localStorage.getItem("companyData") : null;
                      if (!raw) return <p className={darkMode ? "text-gray-400" : "text-gray-500"}>No company data available.</p>;
                      const companyData = typeof raw === "string" ? JSON.parse(raw) : raw;
                      const commencesOn = companyData?.serviceCommencesOn;
                      if (!commencesOn) return <p className={darkMode ? "text-gray-400" : "text-gray-500"}>Service commencement date not set.</p>;
                      const date = new Date(commencesOn);
                      if (Number.isNaN(date.getTime())) return <p className={darkMode ? "text-gray-400" : "text-gray-500"}>Invalid date.</p>;
                      const displayText = date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
                      return (
                        <div className={`p-4 rounded-none border ${darkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}>
                          <h3 className="text-lg font-semibold mb-2">General Settings</h3>
                          <p className={`text-sm mb-4 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                            Company-level setting (read-only).
                          </p>
                          <div className="space-y-1">
                            <label className={`block text-sm font-medium ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                              Service Commences On
                            </label>
                            <div
                              className={`inline-block px-4 py-2 rounded border ${darkMode ? "bg-gray-600 border-gray-500 text-gray-200" : "bg-white border-gray-300 text-gray-800"}`}
                              title="Read-only: set at company level"
                            >
                              {displayText}
                            </div>
                            <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                              View-only. This value is configured at superadmin level.
                            </p>
                          </div>
                        </div>
                      );
                    } catch (e) {
                      return <p className={darkMode ? "text-gray-400" : "text-gray-500"}>Unable to load company data.</p>;
                    }
                  })()
                )}

                {payrollSettingsTab === "investmentDeclaration" && (
                <>
                {/* Investment Declaration Settings */}
                <div
                  className={`p-4 rounded-none border ${
                    darkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Investment Declaration Settings
                  </h3>

                  {/* Enable Investment Declaration */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-base font-medium mb-1">
                          Enable Investment Declaration
                        </h4>
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Allow employees and admins to enter investment
                          declarations for HRA, Section 80C, VIA-deductions, and
                          House Property
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableInvestmentDeclaration}
                          onChange={(e) =>
                            setEnableInvestmentDeclaration(e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 rounded-full peer ${
                            darkMode ? "bg-gray-600" : "bg-gray-200"
                          } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-redhalo-red`}
                        ></div>
                      </label>
                    </div>

                    {enableInvestmentDeclaration && (
                      <div className="ml-4 pl-4 border-l-2 border-redhalo-red">
                        <div className="mb-3">
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Enablement Mode
                          </label>
                          <div className="flex space-x-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="manual"
                                checked={investmentDeclarationMode === "manual"}
                                onChange={(e) =>
                                  setInvestmentDeclarationMode(e.target.value)
                                }
                                className="mr-2"
                              />
                              <span
                                className={`text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Manual
                              </span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="auto"
                                checked={investmentDeclarationMode === "auto"}
                                onChange={(e) =>
                                  setInvestmentDeclarationMode(e.target.value)
                                }
                                className="mr-2"
                              />
                              <span
                                className={`text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Auto (Monthly)
                              </span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="always"
                                checked={investmentDeclarationMode === "always"}
                                onChange={(e) =>
                                  setInvestmentDeclarationMode(e.target.value)
                                }
                                className="mr-2"
                              />
                              <span
                                className={`text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Always Enable
                              </span>
                            </label>
                          </div>
                        </div>

                        {investmentDeclarationMode === "manual" && (
                          <div>
                            <div className="mb-3">
                              <label
                                className={`block text-sm font-medium mb-2 ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                End Date
                              </label>
                              <DatePicker
                                value={investmentDeclarationManualEndDate}
                                onChange={(e) =>
                                  setInvestmentDeclarationManualEndDate(
                                    e.target.value
                                  )
                                }
                                darkMode={darkMode}
                                min={new Date().toISOString().slice(0,10)}
                                className={`w-full px-3 py-2 border rounded-none ${
                                  darkMode
                                    ? "bg-gray-600 border-gray-500 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 `}
                              />
                            </div>
                            <p
                              className={`text-xs mt-2 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Investment declarations will be enabled until the
                              selected end date
                            </p>
                          </div>
                        )}

                        {investmentDeclarationMode === "auto" && (
                          <div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label
                                  className={`block text-sm font-medium mb-2 ${
                                    darkMode ? "text-gray-300" : "text-gray-700"
                                  }`}
                                >
                                  From Day
                                </label>
                                <select
                                  value={investmentDeclarationFromDate}
                                  onChange={(e) => {
                                    setInvestmentDeclarationFromDate(
                                      e.target.value
                                    );
                                    // Ensure to date is not before from date
                                    if (
                                      parseInt(e.target.value) >
                                      parseInt(investmentDeclarationToDate)
                                    ) {
                                      setInvestmentDeclarationToDate(
                                        e.target.value
                                      );
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-none ${
                                    darkMode
                                      ? "bg-gray-600 border-gray-500 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35`}
                                >
                                  {Array.from({ length: 31 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                      {i + 1}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label
                                  className={`block text-sm font-medium mb-2 ${
                                    darkMode ? "text-gray-300" : "text-gray-700"
                                  }`}
                                >
                                  To Day
                                </label>
                                <select
                                  value={investmentDeclarationToDate}
                                  onChange={(e) =>
                                    setInvestmentDeclarationToDate(
                                      e.target.value
                                    )
                                  }
                                  className={`w-full px-3 py-2 border rounded-none ${
                                    darkMode
                                      ? "bg-gray-600 border-gray-500 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35`}
                                >
                                  {Array.from({ length: 31 }, (_, i) => {
                                    const day = i + 1;
                                    // Only show days that are >= from date
                                    if (
                                      day >=
                                      parseInt(investmentDeclarationFromDate)
                                    ) {
                                      return (
                                        <option key={day} value={day}>
                                          {day}
                                        </option>
                                      );
                                    }
                                    return null;
                                  })}
                                </select>
                              </div>
                            </div>
                            <p
                              className={`text-xs mt-2 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Investment declarations will be automatically
                              enabled from {investmentDeclarationFromDate}th to{" "}
                              {investmentDeclarationToDate}th of every month
                            </p>
                          </div>
                        )}

                        {investmentDeclarationMode === "always" && (
                          <div>
                            <p
                              className={`text-sm ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Investment declarations will be permanently
                              enabled for all employees
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Investment Declaration with Proof */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-base font-medium mb-1">
                          Investment Declaration with Proof
                        </h4>
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Require employees to upload proof documents for each
                          investment declaration item
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableInvestmentDeclarationWithProof}
                          onChange={(e) =>
                            setEnableInvestmentDeclarationWithProof(
                              e.target.checked
                            )
                          }
                          className="sr-only peer"
                        />
                        <div
                          className={`w-11 h-6 rounded-full peer ${
                            darkMode ? "bg-gray-600" : "bg-gray-200"
                          } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-redhalo-red`}
                        ></div>
                      </label>
                    </div>

                    {enableInvestmentDeclarationWithProof && (
                      <div className="ml-4 border-l-2 border-emerald-500 pl-4 dark:border-emerald-600">
                        <div className="mb-3">
                          <label
                            className={`block text-sm font-medium mb-2 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Enablement Mode
                          </label>
                          <div className="flex space-x-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="manual"
                                checked={
                                  investmentDeclarationWithProofMode ===
                                  "manual"
                                }
                                onChange={(e) =>
                                  setInvestmentDeclarationWithProofMode(
                                    e.target.value
                                  )
                                }
                                className="mr-2"
                              />
                              <span
                                className={`text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Manual
                              </span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="auto"
                                checked={
                                  investmentDeclarationWithProofMode === "auto"
                                }
                                onChange={(e) =>
                                  setInvestmentDeclarationWithProofMode(
                                    e.target.value
                                  )
                                }
                                className="mr-2"
                              />
                              <span
                                className={`text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Auto (Yearly)
                              </span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                value="always"
                                checked={
                                  investmentDeclarationWithProofMode ===
                                  "always"
                                }
                                onChange={(e) =>
                                  setInvestmentDeclarationWithProofMode(
                                    e.target.value
                                  )
                                }
                                className="mr-2"
                              />
                              <span
                                className={`text-sm ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Always Enable
                              </span>
                            </label>
                          </div>
                        </div>

                        {investmentDeclarationWithProofMode === "manual" && (
                          <div>
                            <div className="mb-3">
                              <label
                                className={`block text-sm font-medium mb-2 ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                End Date
                              </label>
                              <DatePicker
                                min={new Date().toISOString().slice(0,10)}
                                value={
                                  investmentDeclarationWithProofManualEndDate
                                }
                                onChange={(e) =>
                                  setInvestmentDeclarationWithProofManualEndDate(
                                    e.target.value
                                  )
                                }
                                className={`w-full px-3 py-2 border rounded-none ${
                                  darkMode
                                    ? "bg-gray-600 border-gray-500 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35`}
                              />
                            </div>
                            <p
                              className={`text-xs mt-2 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Proof uploads will be enabled until the selected
                              end date
                            </p>
                          </div>
                        )}

                        {investmentDeclarationWithProofMode === "auto" && (
                          <div>
                            <div className="mb-4">
                              <label
                                className={`block text-sm font-medium mb-2 ${
                                  darkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Month
                              </label>
                              <select
                                value={investmentDeclarationWithProofMonth}
                                onChange={(e) =>
                                  setInvestmentDeclarationWithProofMonth(
                                    e.target.value
                                  )
                                }
                                className={`w-full px-3 py-2 border rounded-none ${
                                  darkMode
                                    ? "bg-gray-600 border-gray-500 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35`}
                              >
                                {Array.from({ length: 12 }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>
                                    {getMonthName(i + 1)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label
                                  className={`block text-sm font-medium mb-2 ${
                                    darkMode ? "text-gray-300" : "text-gray-700"
                                  }`}
                                >
                                  From Day
                                </label>
                                <select
                                  value={investmentDeclarationWithProofFromDate}
                                  onChange={(e) => {
                                    setInvestmentDeclarationWithProofFromDate(
                                      e.target.value
                                    );
                                    // Ensure to date is not before from date
                                    if (
                                      parseInt(e.target.value) >
                                      parseInt(
                                        investmentDeclarationWithProofToDate
                                      )
                                    ) {
                                      setInvestmentDeclarationWithProofToDate(
                                        e.target.value
                                      );
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-none ${
                                    darkMode
                                      ? "bg-gray-600 border-gray-500 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35`}
                                >
                                  {Array.from({ length: 31 }, (_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                      {i + 1}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label
                                  className={`block text-sm font-medium mb-2 ${
                                    darkMode ? "text-gray-300" : "text-gray-700"
                                  }`}
                                >
                                  To Day
                                </label>
                                <select
                                  value={investmentDeclarationWithProofToDate}
                                  onChange={(e) =>
                                    setInvestmentDeclarationWithProofToDate(
                                      e.target.value
                                    )
                                  }
                                  className={`w-full px-3 py-2 border rounded-none ${
                                    darkMode
                                      ? "bg-gray-600 border-gray-500 text-white"
                                      : "bg-white border-gray-300 text-gray-900"
                                  } focus:outline-none focus:ring-2 focus:ring-redhalo-red/35`}
                                >
                                  {Array.from({ length: 31 }, (_, i) => {
                                    const day = i + 1;
                                    // Only show days that are >= from date
                                    if (
                                      day >=
                                      parseInt(
                                        investmentDeclarationWithProofFromDate
                                      )
                                    ) {
                                      return (
                                        <option key={day} value={day}>
                                          {day}
                                        </option>
                                      );
                                    }
                                    return null;
                                  })}
                                </select>
                              </div>
                            </div>
                            <p
                              className={`text-xs mt-2 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Proof uploads will be automatically enabled from{" "}
                              {investmentDeclarationWithProofFromDate}th to{" "}
                              {investmentDeclarationWithProofToDate}th of{" "}
                              {getMonthName(
                                investmentDeclarationWithProofMonth
                              )}{" "}
                              every year
                            </p>
                          </div>
                        )}

                        {investmentDeclarationWithProofMode === "always" && (
                          <div>
                            <p
                              className={`text-sm ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Proof uploads will be permanently enabled for all
                              employees
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tax Regime Lock - ON by default; can be turned off only when Investment or Proof is ON */}
                  {(() => {
                    const taxRegimeLockDisabled =
                      !enableInvestmentDeclaration &&
                      !enableInvestmentDeclarationWithProof;
                    const effectiveTaxRegimeLock = taxRegimeLockDisabled
                      ? true
                      : enableTaxRegimeLock;
                    return (
                      <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-base font-medium mb-1">
                              Tax Regime Selection Lock
                            </h4>
                            <p
                              className={`text-sm ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              Lock or unlock the ability for employees to change
                              their tax regime selection
                            </p>
                            {taxRegimeLockDisabled && (
                              <p
                                className={`text-xs mt-1 ${
                                  darkMode ? "text-amber-400" : "text-amber-600"
                                }`}
                              >
                                Turn on Investment Declaration or Proof to unlock this setting
                              </p>
                            )}
                          </div>
                          <label
                            className={`relative inline-flex items-center ${
                              taxRegimeLockDisabled
                                ? "cursor-not-allowed opacity-90"
                                : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={effectiveTaxRegimeLock}
                              disabled={taxRegimeLockDisabled}
                              onChange={(e) =>
                                setEnableTaxRegimeLock(e.target.checked)
                              }
                              className="sr-only peer"
                            />
                            <div
                              className={`w-11 h-6 rounded-full peer ${
                                darkMode ? "bg-gray-600" : "bg-gray-200"
                              } peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600 peer-disabled:opacity-90`}
                            ></div>
                          </label>
                        </div>
                        <p
                          className={`text-xs mt-2 ${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {effectiveTaxRegimeLock
                            ? "Tax regime selection is currently locked - employees cannot change their tax regime"
                            : "Tax regime selection is currently unlocked - employees can change their tax regime"}
                        </p>
                      </div>
                    );
                  })()}
                </div>
                </>
                )}

              </div>

              {/* Action Buttons - only for Investment Declaration tab; Save disabled when tax regime lock is forced */}
              {payrollSettingsTab === "investmentDeclaration" && (() => {
                const taxRegimeLockDisabled =
                  !enableInvestmentDeclaration && !enableInvestmentDeclarationWithProof;
                return (
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600 flex-shrink-0">
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-none ${
                    darkMode
                      ? "bg-gray-600 text-white hover:bg-gray-500"
                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  disabled={savingSettings || taxRegimeLockDisabled}
                  onClick={async () => {
                    setSavingSettings(true);
                    try {
                      // Convert frontend mode values to backend enum values
                      const getBackendInvestmentMode = (mode) => {
                        switch(mode?.toLowerCase()) {
                          case 'auto': return 'AUTO_MONTHLY';
                          case 'always': return 'ALWAYS';
                          case 'manual': 
                          default: return 'MANUAL';
                        }
                      };

                      const getBackendProofMode = (mode) => {
                        switch(mode?.toLowerCase()) {
                          case 'auto': return 'AUTO_YEARLY';
                          case 'always': return 'ALWAYS';
                          case 'manual': 
                          default: return 'MANUAL';
                        }
                      };

                      const taxRegimeLockDisabled =
                        !enableInvestmentDeclaration &&
                        !enableInvestmentDeclarationWithProof;
                      const effectiveTaxRegimeLock = taxRegimeLockDisabled
                        ? true
                        : enableTaxRegimeLock;

                      const settingsData = {
                        isInvestmentEnabled: enableInvestmentDeclaration,
                        investmentMode: getBackendInvestmentMode(investmentDeclarationMode),
                        investmentFromDay: investmentDeclarationFromDate,
                        investmentToDay: investmentDeclarationToDate,
                        investmentEndDate: normalizeOptionalDate(
                          investmentDeclarationManualEndDate
                        ),
                        isProofEnabled: enableInvestmentDeclarationWithProof,
                        proofMode: getBackendProofMode(investmentDeclarationWithProofMode),
                        proofMonth: investmentDeclarationWithProofMonth,
                        proofFromDay: investmentDeclarationWithProofFromDate,
                        proofToDay: investmentDeclarationWithProofToDate,
                        proofEndDate: normalizeOptionalDate(
                          investmentDeclarationWithProofManualEndDate
                        ),
                        isTaxRegimeLocked: effectiveTaxRegimeLock,
                      };

                      console.log('🔍 Debug - Saving payroll settings:', settingsData);
                      const response = await payrollService.upsertPayrollSettings(settingsData);
                      
                      if (response.success) {
                        applyPayrollSettingsToState(response.data);
                        toast.success("Settings saved successfully!");
                        setShowSettingsModal(false);
                      } else {
                        toast.error(
                          "Failed to save settings: " +
                            (response.message || "Unknown error")
                        );
                      }
                    } catch (error) {
                      console.error("Error saving settings:", error);
                      const message =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to save settings. Please try again.";
                      toast.error(message);
                    } finally {
                      setSavingSettings(false);
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-none transition-colors ${
                    savingSettings || taxRegimeLockDisabled
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : darkMode
                      ? "rh-btn-primary hover:opacity-95"
                      : "rh-btn-primary hover:opacity-95"
                  }`}
                >
                  {savingSettings ? "Saving..." : "Save Settings"}
                </button>
              </div>
                );
              })()}
                </div>
            </div>
          </div>
        )}

        {/* Bulk Upload Investments Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div
              className={`rounded-none shadow-xl w-[700px] h-[600px] flex flex-col ${
                darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
              }`}
            >
              {/* Header - Fixed */}
              <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-redhalo-mint/50 to-gray-panel flex-shrink-0">
                <h2 className="text-xl font-bold text-gray-800">
                  Bulk upload Investments
                </h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className={`${
                    darkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  } transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-grow overflow-y-auto p-6 bg-gray-50">
                <div className="space-y-4">
                  <p
                    className={`text-sm ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Download the unified template with separate columns for Old
                    and New tax regimes:
                  </p>

                  <div className="space-y-3">
                    {/* Unified Template */}
                    <div
                      className={`p-4 rounded-none border ${
                        darkMode
                          ? "bg-gray-700 border-gray-600"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold mb-2">
                            Unified Investment Declaration Template
                          </h3>
                          <p
                            className={`text-sm ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                          >
                            Download template with separate columns for Old and
                            New tax regimes. Fill Old Regime columns if Tax
                            Regime = Old, fill New Regime columns if Tax Regime
                            = New.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            handleTemplateDownload();
                            setShowTemplateModal(false);
                          }}
                          className={`px-4 py-2 text-sm font-medium rounded-none ${
                            darkMode
                              ? "rh-btn-primary hover:opacity-95"
                              : "rh-btn-primary hover:opacity-95"
                          } transition-colors`}
                        >
                          Download Unified Template
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div
                    className={`p-4 rounded-none ${
                      darkMode
                        ? "bg-redhalo-navy/30 border border-redhalo-navy"
                        : "bg-redhalo-mint/40 border border-redhalo-teal/60"
                    }`}
                  >
                    <h4 className="font-semibold mb-2 text-redhalo-navy">
                      Instructions:
                    </h4>
                    <ul
                      className={`text-sm space-y-1 ${
                        darkMode ? "text-redhalo-mint" : "text-redhalo-navy"
                      }`}
                    >
                      <li>
                        • Fill in the employee details and select Tax Regime
                        (Old/New)
                      </li>
                      <li>
                        • If Tax Regime = Old: Fill only the "(Old)" columns,
                        leave "(New)" columns empty
                      </li>
                      <li>
                        • If Tax Regime = New: Fill only the "(New)" columns,
                        leave "(Old)" columns empty
                      </li>
                      <li>
                        • Save the file and use the "Bulk Import" option to
                        upload
                      </li>
                    </ul>
                  </div>

                  {/* File Upload Section */}
                  <div
                    className={`p-4 rounded-none border ${
                      darkMode
                        ? "bg-gray-700 border-gray-600"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <h4 className="font-semibold mb-3">
                      Upload Filled Template
                    </h4>
                    <label
                      className={`block w-full px-4 py-3 border-2 border-dashed rounded-none cursor-pointer transition-colors ${
                        darkMode
                          ? "border-gray-500 hover:border-gray-400 hover:bg-gray-600"
                          : "border-gray-300 hover:border-gray-400 hover:bg-gray-100"
                      }`}
                    >
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          handleBulkImport(e);
                          setShowTemplateModal(false);
                        }}
                        accept=".csv,.xlsx,.xls"
                      />
                      <div className="text-center">
                        <FileUp className="mx-auto h-8 w-8 mb-2 text-gray-400" />
                        <p
                          className={`text-sm ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Click to select Excel file (.xlsx, .xls) or CSV file
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Supported formats: .xlsx, .xls, .csv
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer Action Buttons - Fixed */}
              <div className="border-t p-6 bg-white flex justify-end space-x-3 flex-shrink-0">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-none ${
                    darkMode
                      ? "bg-gray-600 text-white hover:bg-gray-500"
                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  } transition-colors`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Download Confirmation Modal */}
        {showDownloadConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
            <div
              className={`p-6 rounded-lg shadow-xl max-w-md w-full mx-4 ${
                darkMode
                  ? "bg-[#2C2C2C] text-[#E0E0E0]"
                  : "bg-white text-gray-900"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3
                  className={`text-lg font-semibold ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                  }`}
                >
                  Download Warning
                </h3>
                <button
                  onClick={() => setShowDownloadConfirm(false)}
                  className={`${
                    darkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  } transition-colors`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                      darkMode ? "bg-amber-950/80" : "bg-amber-100"
                    }`}
                  >
                    <span className="text-lg text-amber-600 dark:text-amber-400">⚠️</span>
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-sm ${
                        darkMode ? "text-gray-400" : "text-gray-700"
                      }`}
                    >
                      Only <span className="font-semibold">Old Regime</span> investment
                      declarations will be downloaded.
                    </p>
                    <p
                      className={`text-sm mt-2 ${
                        darkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      New Regime data will not be included in this export.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDownloadConfirm(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-none transition-colors ${
                    darkMode
                      ? "bg-gray-700 text-[#E0E0E0] hover:bg-gray-600"
                      : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDownload}
                  className={`px-4 py-2 text-sm font-medium rounded-none transition-colors ${
                    darkMode
                      ? "rh-btn-primary hover:opacity-95"
                      : "rh-btn-primary hover:opacity-95"
                  }`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters modal */}
        {showFilterPopup && (
          <Filters
            onApply={(_employees, formFilters, params) => {
              // Use params (API-formatted keys) for backend queries
              setAppliedFilters(params || {});
              // Persist last-applied form state so it "stays" until Clear + Apply
              setAppliedFiltersForm(formFilters || {});
              setShowFilterPopup(false);
            }}
            onClose={() => setShowFilterPopup(false)}
            darkMode={darkMode}
            initialFilters={appliedFiltersForm}
          />
        )}

        <AttachmentPreviewModal
          isOpen={incomeTaxGuidePreview.isOpen}
          url={incomeTaxGuidePreview.url}
          downloadUrl={incomeTaxGuidePreview.downloadUrl}
          fileName={incomeTaxGuidePreview.fileName}
          onClose={() =>
            {
              if (incomeTaxGuideBlobUrlRef.current) {
                URL.revokeObjectURL(incomeTaxGuideBlobUrlRef.current);
                incomeTaxGuideBlobUrlRef.current = null;
              }
              setIncomeTaxGuidePreview({
                isOpen: false,
                url: null,
                downloadUrl: null,
                fileName: "",
              });
            }
          }
        />
      </div>
    </div>
  );
}
